<template>
  <div class="cp" v-if="visible" :class="{ 'cp--window': windowMode }">
    <!-- ── #2091 r4 — undocked: the chat lives in another window, so the main
         page shows only a way to bring it back and otherwise gets its full
         width (no panel, no reserved gutter, left rail restored). ─────────── -->
    <div v-if="!windowMode && undocked" class="cp__away" data-testid="chat-undocked-pill">
      <i class="fa fa-external-link" aria-hidden="true"></i>
      <span>Chat is open in another window</span>
      <button type="button" class="cp__awayb" data-testid="chat-dock" @click="dock()">Dock</button>
    </div>

    <!-- ── the docked launcher, when the panel is closed ─────────────────── -->
    <!-- #2101 — the launcher blinks with the tab title, on the same switch and
         stopped by the same things. One state, two places it shows. -->
    <button v-if="!windowMode && !undocked && collapsed" type="button" class="cp__rail"
            :class="{ 'cp__railflash': flashing }" data-testid="chat-launcher"
            @click="collapsed = false; stopFlash()">
      <i class="fa fa-comment" aria-hidden="true"></i>
      <span>Chat</span>
      <span v-if="unreadTotal" class="cp__railn">{{ unreadTotal }}</span>
    </button>

    <!-- ── the 60-second toast ──────────────────────────────────────────── -->
    <div v-if="toast && !windowMode && !undocked" class="cp__toast" role="status" aria-live="polite" data-testid="chat-toast">
      <div class="cp__toasth">
        <span>New message in</span>
        <!-- #2098 — the `#` is part of `where` now, because a conversation does
             not have one: "New message in #Ada Lovelace" is not a channel. -->
        <span class="cp__toastw">{{ toast.where }}</span>
        <span class="cp__toastcd">{{ toastLeft }} s</span>
      </div>
      <div class="cp__toastb">
        <span class="cp__av" :style="{ background: toast.who.c }">{{ toast.who.i }}</span>
        <div>
          <div class="cp__toastn">{{ toast.who.n }}</div>
          <div class="cp__toastx">{{ toast.body }}</div>
        </div>
      </div>
      <div class="cp__toasta">
        <button type="button" class="cp__btn cp__btn--p" @click="openFromToast()">Open chat</button>
        <button type="button" class="cp__btn" @click="hideToast()">Dismiss</button>
      </div>
      <div class="cp__bar"><i :style="{ width: (toastLeft / 60 * 100) + '%' }"></i></div>
    </div>

    <!-- ── the panel ────────────────────────────────────────────────────── -->
    <aside v-if="windowMode || (!undocked && !collapsed)" class="cp__panel" aria-label="Chat" data-testid="chat-panel">
      <div class="cp__head">
        <button v-if="view === 'channel'" type="button" class="cp__ico" title="Back to channels"
                aria-label="Back to channels" @click="view = 'list'">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>
        </button>
        <div class="cp__title">
          <span class="cp__t1">
            {{ headTitle }}
            <!-- #2098 — the dot travels with the person into the conversation,
                 so it is visible while typing to them and not only in the list. -->
            <span v-if="openDirectChannel" class="cp__hdot"
                  :class="{ 'cp__doton': openDirectOnline }"
                  :title="openDirectOnline ? 'Online' : 'Offline'"
                  data-testid="chat-head-dot"></span>
          </span>
          <span class="cp__t2">{{ headSub }}</span>
        </div>
        <!-- #2101 — sound and flash, for EVERYONE. Deliberately not behind
             `mayAdmin` like the gear beside it: the gear opens Configuration →
             Chat, which is the Client Admin's client-wide settings, and these
             are this person's own browser. Two different things that happen to
             look like settings. -->
        <button v-if="isLive" type="button" class="cp__ico"
                :class="{ 'cp__icoon': notifyOpen }" title="Sound and flash"
                aria-label="Sound and flash" :aria-expanded="notifyOpen ? 'true' : 'false'"
                data-testid="chat-notify-toggle" @click="notifyOpen = !notifyOpen">
          <i class="fa" :class="notify.sound ? 'fa-bell' : 'fa-bell-slash'" aria-hidden="true"></i>
        </button>
        <button v-if="mayAdmin && !windowMode" type="button" class="cp__ico" title="Chat settings"
                aria-label="Chat settings" data-testid="chat-gear" @click="openSettings()">
          <i class="fa fa-cog" aria-hidden="true"></i>
        </button>
        <!-- #2091 r4 — undock in the docked panel, dock in the pop-out. One
             control, two directions, so there is never a window with no way
             back. -->
        <button v-if="!windowMode" type="button" class="cp__ico" title="Open chat in its own window"
                aria-label="Open chat in its own window" data-testid="chat-undock" @click="undock()">
          <i class="fa fa-external-link" aria-hidden="true"></i>
        </button>
        <button v-else type="button" class="cp__ico" title="Dock chat back into the page"
                aria-label="Dock chat back into the page" data-testid="chat-redock" @click="dockFromWindow()">
          <i class="fa fa-compress" aria-hidden="true"></i>
        </button>
        <button v-if="!windowMode" type="button" class="cp__ico" title="Collapse chat" aria-label="Collapse chat"
                @click="collapsed = true">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- #2091 r5 — VIEW AS. A preview-only strip, and it says so.
           Ed asked to see the EMPLOYER outside-hours state. The panel otherwise
           takes its chat role from the signed-in CS3 role, so a reviewer signed
           in as a Super Admin is `cs_admin` — and CS staff are never restricted
           by a client's schedule, which means the read-only state is invisible
           to the very person reviewing it. Named people are listed as well as
           roles because "person beats role beats schedule" cannot be shown by
           picking a role alone. -->
      <!-- ⚠ #2096 — GONE IN A LIVE BUILD. Looking through another role's eyes
           is meaningful against invented data and dangerous against real data:
           the channel list and the composer would claim a role the server did
           not grant, so the panel would show a state the user cannot actually
           be in, and every post would still go out as themselves. -->
      <div v-if="!isLive" class="cp__viewas" data-testid="chat-viewas">
        <label class="cp__vl" for="cp-viewas">Preview as</label>
        <select id="cp-viewas" class="cp__vs" v-model="viewAs" data-testid="chat-viewas-select">
          <optgroup label="Role">
            <option v-for="r in previewRoles" :key="'r:' + r" :value="'r:' + r">
              {{ rolePlural[r] || roleLabel[r] || r }}
            </option>
          </optgroup>
          <optgroup v-if="namedPeople.length" label="Named individually">
            <option v-for="x in namedPeople" :key="'p:' + x.eid" :value="'p:' + x.eid">
              {{ x.name }} · {{ roleLabel[x.role] || x.role }}
            </option>
          </optgroup>
        </select>
        <span class="cp__vn">{{ viewAsNote }}</span>
      </div>

      <!-- #2101 items 3-5 — the per-person notification settings.
           localStorage, per browser, with try/catch in `chatNotify`. -->
      <div v-if="notifyOpen" class="cp__notify" data-testid="chat-notify">
        <label class="cp__nrow">
          <input type="checkbox" v-model="notify.sound" data-testid="chat-notify-sound"
                 @change="saveNotify()">
          <span>Sound on a new message</span>
        </label>
        <div class="cp__nrow cp__nvol" :class="{ 'cp__noff': !notify.sound }">
          <label for="cp-vol">Volume</label>
          <input id="cp-vol" type="range" min="0" max="1" step="0.05" :value="notify.volume"
                 :disabled="!notify.sound" data-testid="chat-notify-volume"
                 @input="setVolume($event.target.value)">
          <!-- Hearing it is the only way to set it, and pressing this button is
               itself the user gesture that lets the browser play audio at all. -->
          <button type="button" class="cp__ntest" :disabled="!notify.sound"
                  data-testid="chat-notify-test" @click="playChime()">Test</button>
        </div>
        <label class="cp__nrow">
          <input type="checkbox" v-model="notify.flash" data-testid="chat-notify-flash"
                 @change="saveNotify()">
          <span>Flash the tab and the chat button</span>
        </label>
        <div class="cp__nnote">
          Saved in this browser only, for you. Some browsers will not play a sound until you
          have clicked on the page.
        </div>
      </div>

      <!-- ── #2101 item 1 — OFFICE HOURS, AT THE TOP, ALWAYS ──────────────
           Ed: "office hours are posted at the top of the chat box."

           Shown whatever the state is, including "Open 24 hours" for a client
           with no schedule: a strip that appeared only when the office was shut
           would read as the panel breaking, and the hours are what a crew wants
           to know before they type.

           The timezone is printed because it is the CLIENT's, not the reader's —
           a dispatcher in Vancouver looking at a New York local needs to know
           which clock these times are on. `chatHours` evaluates them in that
           zone, which is the whole reason this is not the preview's helper. -->
      <div v-if="hours" class="cp__hours" :class="{ 'cp__hoursshut': !hours.openNow && hoursBindMe }"
           data-testid="chat-hours">
        <div class="cp__hrow">
          <i class="fa fa-clock-o" aria-hidden="true"></i>
          <strong data-testid="chat-hours-today">{{ hours.todayText }}</strong>
          <span v-if="hours.configured" class="cp__hstate"
                :class="hours.openNow ? 'cp__hopen' : 'cp__hshut'" data-testid="chat-hours-state">
            {{ hours.openNow ? 'open now' : 'closed now' }}
          </span>
          <span v-if="!hours.openNow && hours.reopens" class="cp__hnext">
            reopens {{ hours.reopens }}
          </span>
          <button type="button" class="cp__hmore" :aria-expanded="hoursOpen ? 'true' : 'false'"
                  data-testid="chat-hours-week-toggle" @click="hoursOpen = !hoursOpen">
            {{ hoursOpen ? 'Hide week' : 'Week' }}
          </button>
        </div>
        <div v-if="hoursOpen" class="cp__hweek" data-testid="chat-hours-week">
          <div v-for="d in hours.week" :key="d.key" class="cp__hday" :class="{ 'cp__htoday': d.today }">
            <span class="cp__hdn">{{ d.short }}</span>
            <span class="cp__hdt" :class="{ 'cp__hdshut': !d.open }">{{ d.text }}</span>
          </div>
          <div class="cp__htz">
            Times are {{ hours.tz }}{{ hours.configured ? '' : ' — no schedule set' }}
          </div>
        </div>
      </div>

      <div v-if="popupBlocked" class="cp__blocked" data-testid="chat-popup-blocked">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
        <span>
          Your browser blocked the chat window, so chat stayed here. Allow pop-ups for this site and
          try Undock again.
        </span>
      </div>

      <div class="cp__body" ref="body">
        <!-- channel list -->
        <template v-if="view === 'list'">
          <!-- ── #2098 — SEARCH FOR A PERSON, AND MESSAGE THEM ─────────────
               Ed asked for "a search box for individuals". The list it draws
               is the SERVER's answer: cs3-chat's DirectReach applies the
               who-can-be-found rule (own local plus CS staff, or only
               switched-on groups when All Members is off), and nothing is
               filtered again here — a second reading of that rule in the
               browser could only ever disagree with the one that decides. -->
          <!-- ── #2118 — YOUR CONTACTS (employers only) ──────────────────
               Ed, as employer5 on stage-: the employer's window showed only
               Search and Support, "this is not inuitive at all." An employer
               knows which locals they work with, not which people at them, so
               search is the wrong affordance for the one role that cannot
               guess a name.

               ⚠ THE ROLE DECIDES WHETHER THIS EXISTS, NOT THE LIST. A Member
               and an employer with no switched-on client both get `[]` from
               the server; `showsContacts` is what keeps every Member in the
               fleet from seeing an empty "Your contacts" heading.

               The rows are the SEARCH RESULT button, unchanged, and the click
               is `pickPerson` — the same #2098 path that opens a DM, checks
               reach live and reports a 403 in those words. -->
          <div v-if="isLive && showsContacts" class="cp__find" data-testid="chat-contacts">
            <div class="cp__grph"><span>Your contacts</span></div>

            <div v-if="contactsState === 'loading'" class="cp__findn">Loading…</div>
            <div v-else-if="contactsState === 'failed'" class="cp__finde"
                 data-testid="chat-contacts-error">
              {{ contactsErrorText }}
            </div>
            <div v-else-if="!contactGroups.length" class="cp__findn"
                 data-testid="chat-contacts-none">
              No contacts yet. Your clients haven't turned on chat.
            </div>

            <template v-for="g in contactGroups">
              <div :key="g.key" class="cp__grph cp__ccgrp"><span>{{ g.title }}</span></div>
              <button v-for="p in g.people" :key="g.key + ':' + p.uid" type="button"
                      class="cp__per" :disabled="opening === p.uid"
                      data-testid="chat-contact-result" @click="pickPerson(p)">
                <span class="cp__av cp__peav" :style="{ background: p.colour }">
                  {{ p.initials }}
                  <span class="cp__dot" :class="{ 'cp__doton': isOnline(p.uid) }"
                        :title="isOnline(p.uid) ? 'Online' : 'Offline'"></span>
                </span>
                <span class="cp__pemain">
                  <span class="cp__pen">{{ p.name }}</span>
                  <span class="cp__per2">{{ roleLabel[p.role] || p.role }}</span>
                </span>
                <span class="cp__pego">{{ opening === p.uid ? 'Opening…' : 'Message' }}</span>
              </button>
            </template>
          </div>

          <div v-if="isLive" class="cp__find" data-testid="chat-find">
            <div class="cp__findr">
              <i class="fa fa-search" aria-hidden="true"></i>
              <input type="search" class="cp__findi" v-model="peopleQuery"
                     :placeholder="'Find someone to message'"
                     aria-label="Find someone to message" data-testid="chat-find-input"
                     @input="onSearchInput()" @focus="searchOpen = true">
              <button v-if="peopleQuery" type="button" class="cp__findx" title="Clear"
                      aria-label="Clear the search" data-testid="chat-find-clear"
                      @click="closeSearch()">
                <i class="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>

            <div v-if="peopleState === 'searching' || peopleState === 'typing'" class="cp__findn">
              Searching…
            </div>
            <div v-else-if="peopleState === 'failed'" class="cp__finde" data-testid="chat-find-error">
              {{ peopleErrorText }}
            </div>
            <div v-else-if="peopleState === 'done' && !people.length" class="cp__findn"
                 data-testid="chat-find-none">
              Nobody you can message matches that.
            </div>
            <div v-else-if="peopleQuery && peopleState === 'idle'" class="cp__findn">
              Type at least {{ minQuery }} characters.
            </div>

            <!-- ⚠ The dot is absent for most people and that is correct: the
                 Realtime Database rules refuse a read for anyone who has never
                 opened chat, which `chatPresence` records as offline without
                 saying anything. An absent dot means "not online", never
                 "broken". -->
            <button v-for="p in people" :key="'p:' + p.uid" type="button" class="cp__per"
                    :disabled="opening === p.uid" data-testid="chat-find-result"
                    @click="pickPerson(p)">
              <span class="cp__av cp__peav" :style="{ background: p.colour }">
                {{ p.initials }}
                <span class="cp__dot" :class="{ 'cp__doton': isOnline(p.uid) }"
                      :title="isOnline(p.uid) ? 'Online' : 'Offline'"
                      :data-testid="isOnline(p.uid) ? 'chat-dot-on' : 'chat-dot-off'"></span>
              </span>
              <span class="cp__pemain">
                <span class="cp__pen">{{ p.name }}</span>
                <span class="cp__per2">
                  {{ roleLabel[p.role] || p.role }}
                  <span v-if="isStaffPerson(p.role)" class="cp__pecs">CallSteward</span>
                </span>
              </span>
              <span class="cp__pego">{{ opening === p.uid ? 'Opening…' : 'Message' }}</span>
            </button>

            <div v-if="directError" class="cp__finde" data-testid="chat-direct-error">
              {{ directErrorText }}
            </div>
          </div>

          <div v-for="g in groupsShown" :key="g.key" class="cp__grp">
            <div class="cp__grph">
              <span>{{ g.title }}</span>
              <span v-if="g.staffOnly" class="cp__staff">staff only</span>
            </div>
            <button v-for="c in g.rows" :key="c.id" type="button"
                    class="cp__ch" :class="{ 'cp__unread': c.unread, 'cp__on': c.id === openId }"
                    @click="openChannel(c.id)">
              <!-- #2098 — a conversation is a person, not a channel: an avatar
                   with its presence dot instead of a `#`. -->
              <span v-if="c.grp === 'direct'" class="cp__av cp__chav"
                    :style="{ background: colourOf(c) }">
                {{ initialsOf(c) }}
                <span class="cp__dot" :class="{ 'cp__doton': isOnline(c.otherUid) }"
                      :title="isOnline(c.otherUid) ? 'Online' : 'Offline'"></span>
              </span>
              <span v-else class="cp__hash">#</span>
              <span class="cp__chmain">
                <span class="cp__chn">
                  {{ c.label }}
                  <span v-if="c.archived" class="cp__arch">archived</span>
                </span>
                <span class="cp__chs">{{ c.last }}</span>
              </span>
              <span class="cp__chr">
                <span class="cp__cht">{{ c.at }}</span>
                <span v-if="c.unread" class="cp__pill">{{ c.unread }}</span>
              </span>
            </button>
          </div>

          <div v-if="role === 'member'" class="cp__note">
            <strong>No global channels.</strong> A Member can never be granted access to a
            CS-world channel. That is a rule, not a setting a CA can change.
          </div>
          <div v-else-if="role === 'steward' && !granted" class="cp__note">
            <strong>No global channels yet.</strong> Stewards and Power Users see CS-world
            channels only once their own Client Admin grants it.
          </div>
        </template>

        <!-- a channel -->
        <template v-else>
          <div v-if="atCap" class="cp__cap">
            <strong>That’s as far back as chat goes.</strong>
            The panel holds the most recent 120 messages. Older messages are available in
            <span class="cp__where">Reports</span> — a Client Admin or Power User can pull them
            for any channel in this client.
          </div>
          <div v-else class="cp__older">
            <button type="button" class="cp__olderb" @click="loadOlder()">Load older messages</button>
            <div class="cp__olderc">showing {{ shownCount }} of 120 the panel can reach</div>
          </div>

          <template v-for="(m, i) in slice">
            <div v-if="m.day !== dayOf(i - 1)" :key="'d' + i" class="cp__day">{{ m.day }}</div>
            <div :key="'m' + i" class="cp__msg" :class="{ 'cp__cont': isCont(i) }">
              <span class="cp__av" :class="{ 'cp__avsp': isCont(i) }"
                    :style="{ background: isCont(i) ? 'transparent' : m.who.c }">{{ isCont(i) ? '' : m.who.i }}</span>
              <div class="cp__mmain">
                <div v-if="!isCont(i)" class="cp__mh">
                  <span class="cp__mn">{{ m.who.n }}</span>
                  <span class="cp__mrole" :class="{ 'cp__staffrole': m.staff }">{{ roleLabel[m.who.r] || m.who.r }}</span>
                  <span class="cp__mt">{{ m.at }}</span>
                </div>
                <!-- Plain text, always: a message must never be interpreted as markup. -->
                <div class="cp__mb">{{ m.body }}</div>
                <div v-if="m.mailtoNote" class="cp__sani">
                  the address was left as text — a user@host link is a common phishing disguise,
                  so it is never linked
                </div>
                <!-- ── #2102 — WHAT WAS ATTACHED ──────────────────────────
                     A thumbnail for JPEG and PNG, a chip for everything else.
                     HEIC gets a chip on purpose: Safari renders it and Chrome
                     and Firefox do not, so an <img> would be a broken-image
                     icon across most of the fleet — and the file still
                     downloads and opens in the OS viewer.

                     ⚠ The thumbnail falls back to the chip whenever the bytes
                     cannot be fetched (no CORS rule on the bucket, a network
                     drop, an expired link). The chip's own open is a top-level
                     navigation, which CORS does not touch, so the file is
                     always reachable even when the preview is not. -->
                <div v-if="m.attachments && m.attachments.length" class="cp__atts">
                  <template v-for="a in m.attachments">
                    <button v-if="a.thumbnail && thumbs[a.id]" :key="'t' + a.id" type="button"
                            class="cp__thumb" :title="a.name + ' · ' + sizeLabel(a.size)"
                            data-testid="chat-attachment-thumb" @click="showLarger(a)">
                      <img :src="thumbs[a.id]" :alt="a.name">
                    </button>
                    <button v-else :key="'c' + a.id" type="button" class="cp__chip"
                            :title="'Open ' + a.name" data-testid="chat-attachment-chip"
                            @click="openAttachment(a)">
                      <i class="fa"
                         :class="a.type === 'application/pdf' ? 'fa-file-pdf-o' : 'fa-file-image-o'"
                         aria-hidden="true"></i>
                      <span class="cp__chipm">
                        <span class="cp__chipn">{{ a.name }}</span>
                        <span class="cp__chips">
                          {{ kindLabel(a.type) }} · {{ sizeLabel(a.size) }}
                          <span v-if="a.thumbnail && fetching[a.id]">· loading…</span>
                        </span>
                      </span>
                      <i class="fa fa-download" aria-hidden="true"></i>
                    </button>
                  </template>
                </div>

                <div v-if="m.link" class="cp__lp">
                  <span class="cp__lpic">{{ m.link.letter }}</span>
                  <div class="cp__lpm">
                    <div class="cp__lpt">{{ m.link.title }}</div>
                    <div class="cp__lph">{{ m.link.host }}</div>
                    <div v-if="m.link.punycode" class="cp__lpw">⚠ {{ m.link.punycode }}</div>
                    <div v-if="m.link.svgRefused" class="cp__sani">
                      the site’s icon was an SVG, which is refused because it can carry script —
                      initial shown instead
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- ── #2108 — CALLSTEWARD SUPPORT, PINNED ─────────────────────────
           Ed, 2026-10-21: "the cs support should exist at the bottom of the
           chat box and always be visible."

           ⚠ OUTSIDE `cp__body`, WHICH IS WHAT "NOT SCROLLED AWAY" MEANS. The
           list above scrolls; this is a sibling of that scroller, so it stays
           on screen however long the channel list is. Putting it inside with a
           `position: sticky` would still move on an overscroll and would sit
           above the list's own last row.

           ⚠ AND IT IS NOT IN `groupsShown`. The roster gives it `grp:
           'support'` (chatSupport.labelSupport) and no heading has that key, so
           it never appears among the client's channels — issue item 1, "It
           doesn't appear among the other channels" — without a second filter
           anywhere.

           List view only: in a conversation the bottom of the panel is the
           composer, and the way back to Support is the same way back to any
           channel. Absent for CS staff, who have no support conversation of
           their own (cs3-chat `SupportChannels::ensure`) and whose roster holds
           everybody else's. -->
      <button v-if="view === 'list' && supportPinned" type="button"
              class="cp__ch cp__pin" :class="{ 'cp__unread': supportPinned.unread }"
              data-testid="chat-support-pinned" @click="openChannel(supportPinned.id)">
        <span class="cp__pinic"><i class="fa fa-life-ring" aria-hidden="true"></i></span>
        <span class="cp__chmain">
          <span class="cp__chn">{{ supportPinned.label }}</span>
          <span class="cp__chs">{{ supportPinned.last || 'Questions about CallSteward' }}</span>
        </span>
        <span class="cp__chr">
          <span class="cp__cht">{{ supportPinned.at }}</span>
          <span v-if="supportPinned.unread" class="cp__pill">{{ supportPinned.unread }}</span>
        </span>
      </button>

      <!-- ── #2102 — a photo, larger. The bytes are already held, so this
           costs nothing and needs no second fetch. ──────────────────────── -->
      <div v-if="lightbox" class="cp__lb" data-testid="chat-lightbox" @click="lightbox = null">
        <img :src="lightbox.url" :alt="lightbox.name">
        <div class="cp__lbn">{{ lightbox.name }}</div>
        <button type="button" class="cp__lbx" aria-label="Close" @click="lightbox = null">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- composer, or why there isn't one -->
      <div v-if="view === 'channel'">
        <!-- ── #2108 — THE SUPPORT CONVERSATION IS ITS OWN STATE ───────────
             Checked FIRST, before the archived / job-window / office-hours
             banners below, because every one of those is about a client's
             channel and this conversation is with CallSteward: Ed's rule is
             that Support is always open to everyone and a client's office
             hours never limit it (issue item 3).

             Three things can show here:
               checking    the availability request is out
               live        the ordinary composer, by falling through
               a ticket    FreeScout is shut or no agent is on, so the person
                           leaves a ticket instead of a message nobody will
                           read until morning

             `not_configured` deliberately falls through to the composer:
             with no FreeScout there is nothing to create a ticket in, and the
             conversation still reaches CallSteward. chatSupport.offersTicket
             says why. -->
        <!-- ── #2108 — THE SUPPORT CONVERSATION'S OWN STATES ──────────────
             Checked FIRST, before the archived / job-window / office-hours
             banners below, because every one of those is about a client's
             channel and this conversation is with CallSteward: Ed's rule is
             that Support is always open to everyone and a client's office
             hours never limit it (issue item 3).

             ⚠ ONLY THE STATES THAT REPLACE THE COMPOSER ARE HERE. When support
             IS live the chain falls through to the ordinary composer at the
             bottom of this block — deliberately, so a support message keeps the
             paperclip, the paste handler, the drop target and the upload queue
             that #2102 built. A second, simpler composer written here would
             have quietly taken attachments away from the one conversation most
             likely to need a screenshot. The note above it is rendered inside
             that composer, not instead of it.

             `not_configured` falls through for the same reason plus one more:
             with no FreeScout there is nothing to create a ticket in, so a form
             here would be a button that answers 503. chatSupport.offersTicket
             says so in one place. -->
        <template v-if="supportPanel">
          <div v-if="supportTicket" class="cp__sup" data-testid="chat-support-ticketed">
            <div class="cp__suph">
              <i class="fa fa-check-circle" aria-hidden="true"></i>
              <strong>Ticket #{{ supportTicket }} is open</strong>
            </div>
            <div class="cp__supb">
              The support team will reply by email. You can close chat — the ticket does not
              need this window to stay open.
            </div>
            <button type="button" class="cp__btn" data-testid="chat-support-ticket-again"
                    @click="newTicket()">Open another ticket</button>
          </div>
          <div v-else-if="supportChecking" class="cp__sup" data-testid="chat-support-checking">
            <div class="cp__suph">
              <i class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>
              <strong>Checking whether support is online…</strong>
            </div>
          </div>
          <div v-else class="cp__sup" data-testid="chat-support-ticket">
            <div class="cp__suph">
              <i class="fa fa-clock-o" aria-hidden="true"></i>
              <strong data-testid="chat-support-reason">{{ supportReason }}</strong>
            </div>
            <div class="cp__supb">
              {{ supportOffer }}
              <span v-if="supportNextOpen" class="cp__supnx">
                Support reopens {{ shortWhen(supportNextOpen) }}.
              </span>
            </div>
            <!-- ⚠ No email on file, and the form says so BEFORE the message is
                 written: cs3-chat creates the ticket FOR an address and refuses
                 without one (issue item 4). -->
            <div v-if="!canTicket" class="cp__supno" data-testid="chat-support-no-email">
              {{ noEmailText }}
            </div>
            <template v-else>
              <textarea v-model="ticketDraft" :maxlength="maxTicketChars"
                        class="cp__supta" placeholder="What do you need help with?"
                        aria-label="Describe what you need help with"
                        data-testid="chat-support-ticket-body"></textarea>
              <div class="cp__supr">
                <div class="cp__supc">{{ ticketDraft.length.toLocaleString('en') }} / {{ maxTicketChars.toLocaleString('en') }}</div>
                <button type="button" class="cp__send" :disabled="!ticketReady || ticketSending"
                        data-testid="chat-support-ticket-send" @click="sendTicket()">
                  {{ ticketSending ? 'Sending…' : 'Submit a ticket' }}
                </button>
              </div>
              <div v-if="ticketError" class="cp__supe" data-testid="chat-support-ticket-error">
                {{ ticketErrorText }}
              </div>
              <!-- The conversation above stays readable either way (issue item
                   2), and a message here is still a legitimate route — an agent
                   reads it when they are next on. -->
              <div class="cp__supalt">
                Earlier messages above stay readable. A ticket is the quicker route while
                the desk is unattended.
              </div>
            </template>
          </div>
        </template>
        <div v-else-if="channel.archived" class="cp__ro" data-testid="chat-archived">
          <i class="fa fa-archive" aria-hidden="true"></i>
          <div>
            <strong>This channel is archived</strong>
            It is read-only for everyone, including Client Admins and CallSteward staff. The
            conversation stays readable here until it ages out, and in Reports after that.
          </div>
        </div>
        <!-- #2091 r3 — a job channel outside its window. Its own banner, not the
             office-hours one: the reason is different, the remedy is different
             (wait for the call, versus ask your CA), and running them together
             would tell the reader neither. -->
        <div v-else-if="jobWindow && jobWindow.state !== 'open'" class="cp__closed" data-testid="chat-job-window">
          <div class="cp__closedh">
            <i class="fa fa-calendar" aria-hidden="true"></i>
            <strong>{{ jobWindow.state === 'before'
              ? 'Chat for this job opens ' + shortWhen(jobWindow.opensAt)
              : 'Chat for this job closed ' + shortWhen(jobWindow.closesAt) }}</strong>
          </div>
          <div class="cp__closedb">
            A job channel is open from 24 hours before the job starts until 24 hours after it ends.
            The conversation stays readable either side.
          </div>
          <div class="cp__fake">Message {{ composeTarget }}</div>
        </div>
        <div v-else-if="!mayPost" class="cp__closed" data-testid="chat-outside-hours">
          <div class="cp__closedh">
            <i class="fa fa-clock-o" aria-hidden="true"></i>
            <strong>Chat is read-only outside office hours{{ reopensAt ? ' — reopens ' + reopensAt : '' }}</strong>
          </div>
          <!-- #2106 — THE CLIENT'S OWN WORDS, where the composer would be. Ed,
               2026-10-21: "client chat box that speaks to users blocked by hours
               should display a client defined message." Text, never HTML: the
               CA types it and cs3-chat stores it as plain text, so `{{ }}` is
               both the rendering and the escaping. `cp__closedm` keeps the line
               breaks they typed and nothing else. -->
          <div class="cp__closedb cp__closedm" data-testid="chat-closed-message">{{ closedText }}</div>
          <div class="cp__fake">Message {{ composeTarget }}</div>
        </div>
        <div v-else class="cp__comp" :class="{ 'cp__atcap': draft.length >= 4000, 'cp__drag': dragging }"
             @dragover.prevent="dragging = mayAttach" @dragleave.prevent="dragging = false"
             @drop.prevent="onDrop($event)">
          <!-- ── #2102 — FILES PICKED BUT NOT YET SENT ──────────────────────
               Each row is its own upload with its own progress and its own
               cancel, because one 10 MB failure should retry one file rather
               than a batch. -->
          <div v-if="pending.length" class="cp__q" data-testid="chat-attach-queue">
            <div v-for="p in pending" :key="p.key" class="cp__qr"
                 :class="{ 'cp__qfail': p.state === 'failed' }">
              <i class="fa" :class="p.type === 'application/pdf' ? 'fa-file-pdf-o' : 'fa-file-image-o'"
                 aria-hidden="true"></i>
              <span class="cp__qn">{{ p.name }}</span>
              <span class="cp__qs">{{ sizeLabel(p.size) }}</span>
              <span v-if="p.state === 'uploading'" class="cp__qbar" :title="p.pct + '%'">
                <i :style="{ width: p.pct + '%' }"></i>
              </span>
              <span v-else-if="p.state === 'ready'" class="cp__qok" title="Ready to send">
                <i class="fa fa-check" aria-hidden="true"></i>
              </span>
              <span v-else class="cp__qe">{{ p.error }}</span>
              <button type="button" class="cp__qx"
                      :title="p.state === 'uploading' ? 'Cancel this upload' : 'Remove'"
                      :aria-label="p.state === 'uploading' ? 'Cancel this upload' : 'Remove this file'"
                      data-testid="chat-attach-remove" @click="removeFile(p.key)">
                <i class="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div v-if="attachError" class="cp__qerr" data-testid="chat-attach-error">{{ attachError }}</div>

          <!-- ── #2108 — the line above a LIVE support composer ────────────
               Empty when support is plainly open: a conversation that works
               needs no explanation. It carries the two states that fall
               through to this composer without being `open` —
               `not_configured` (no FreeScout on this deployment) and `unknown`
               (the availability request itself failed) — because in both the
               person should know that nobody has promised them an agent is
               reading right now. -->
          <div v-if="inSupport && supportNote" class="cp__supline" data-testid="chat-support-note">
            <i class="fa fa-life-ring" aria-hidden="true"></i>
            <span>{{ supportNote }}</span>
          </div>

          <!-- #2103 item 5 — paste an image. Ed pasted a Snipping Tool capture
               and nothing happened; a snip arrives on the clipboard as a file
               with no useful name, which `imagesFromPaste` supplies. Plain-text
               paste is untouched (item 6) — `onPaste` only intervenes when
               there is actually an image. -->
          <textarea v-model="draft" :maxlength="4000" :placeholder="'Message ' + composeTarget"
                    :aria-label="'Message ' + channel.label" data-testid="chat-composer"
                    @paste="onPaste($event)"></textarea>
          <div class="cp__compr">
            <!-- #2102 — the paperclip. Hidden input, because a styled file
                 input is not stylable; `mayAttach` is `mayPost`, so a read-only
                 composer has no paperclip at all (issue item 4). -->
            <template v-if="mayAttach">
              <input ref="fileInput" type="file" class="cp__file" :accept="attachAccept"
                     aria-hidden="true" tabindex="-1" data-testid="chat-attach-input"
                     @change="onFilePicked($event)">
              <button type="button" class="cp__clip" title="Attach a photo or PDF"
                      aria-label="Attach a photo or PDF" data-testid="chat-attach"
                      :disabled="pending.length >= maxAttachments" @click="pickFile()">
                <i class="fa fa-paperclip" aria-hidden="true"></i>
              </button>
            </template>
            <div class="cp__rules">Plain text. 4,000 characters, 5 links, 30 posts a minute.</div>
            <div class="cp__cnt" :class="counterClass">{{ draft.length.toLocaleString('en') }} / 4,000</div>
            <button type="button" class="cp__send"
                    :disabled="(!draft.trim() && !readyAttachments.length) || draft.length >= 4000
                               || sending || uploading"
                    @click="send()">{{ sending ? 'Sending…' : (uploading ? 'Uploading…' : 'Send') }}</button>
          </div>
          <div v-if="draft.length >= 4000" class="cp__compmsg">
            That’s the 4,000-character limit. Shorten the message to send it.
          </div>
          <!-- #2096 — why the last send did not land. Sits with the composer
               rather than in the header because it is about the message the
               user still has in front of them, and the draft is deliberately
               not cleared on a failure. -->
          <div v-else-if="liveError" class="cp__compmsg" data-testid="chat-send-error">
            {{ liveErrorText }}
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
/**
 * #2091 — the right-hand chat panel, behind CHAT_UI_PREVIEW.
 *
 * The approved wireframe (cs3-chat !4 round 4) rebuilt as a real component so Ed
 * can see it on real CS3 pages.
 *
 * ⚠ NO BACK END, AND NOTHING LEAVES THE BROWSER. There is no chat service yet
 * (cs3-chat #6-#10, #18): this file imports no service, makes no request, and
 * persists nothing. "Sending" appends to a local array that a reload discards.
 * Every name, channel and message comes from `chatPreview.js` and is invented.
 *
 * The one behaviour worth stating: messages render through Vue text
 * interpolation, never `v-html`, so a message cannot be interpreted as markup —
 * the rule the real panel has to keep, and one sample message exercises it.
 */
import {
    CHANNELS, GROUPS, PREVIEW_ROLES, ROLE_LABEL, ROLE_PLURAL, TODAY,
    buildHistory, canAdminChat, chatLiveEnabled, chatUiEnabled, historyFor,
    isOutsideHours, jobChatWindow, nextOpenLabel, previewStore, shortWhen, windowLabel,
} from '../../chatPreview.js';
// #2096 — the real chat service. Everything live goes through this module; the
// component holds no knowledge of the API or of Firestore.
import {
    ChatUnavailable, applyChannelDoc, attachPresence, attachmentUrl, colourFor, fetchAttachment,
    fetchChannels, fetchClientSettings, fetchContacts, fetchSupportAvailability, fetchUnread, initialsFor,
    listenToChannel, listenToChannelDocs, markRead, openDirect, resetChatSession, searchPeople,
    sendMessage, signIn, submitSupportTicket, uploadAttachment,
} from '../../chatService.js';
/*
 * #2110 — the unread counts come from the server and the read mark goes back
 * to it. The rules (which channel counts as "looking", when the lease is
 * renewed, how a burst of triggers becomes one request) take their timers as
 * arguments and are tested under node; this component only wires them.
 */
import {
    applyMarkResult, applyUnreadCounts, createReadMarker, createRefresher, isLooking,
    newestShownId,
} from '../../chatUnread.js';
/*
 * #2102 — what may be attached, what a refusal says, and the fetched-file
 * cache. Pure and separate for the usual reason: the component cannot be loaded
 * under node, and every mistake available here is silent — a refused file with
 * no explanation, an object URL leaked for the life of the tab, or a
 * five-minute pre-signed URL cached until it 403s.
 */
import {
    ACCEPT_ATTR, MAX_PER_MESSAGE, createFileCache, imagesFromPaste, kindLabel, refusalFor,
    refusalText, sizeLabel,
} from '../../chatAttachments.js';
/*
 * #2098 — the person search and the direct conversation's panel identity.
 * Pure, and separate for the same reason as everything else in this component's
 * imports: the rules are where the mistakes are, and the component cannot be
 * loaded under node.
 */
import { DEBOUNCE_MS, MIN_QUERY, isStaffRole, labelDirect, searchable } from '../../chatPeople.js';
/*
 * #2118 — "Your contacts". Pure and separate for the same reason as the rest of
 * this list: the role gate, the null-name fallback and the grouping are all
 * silent when wrong, and `chatContacts.js` is where they are tested.
 */
import { showsContacts } from '../../chatContacts.js';
/*
 * #2108 — CallSteward Support. Pure and separate for the same reason as
 * everything else in this list: the decisions worth testing are which
 * availability reason offers a ticket, which one must NOT (there is no
 * FreeScout to ticket into), and what each refusal says to the person.
 */
import {
    MAX_TICKET_CHARS, availabilityFailed, isSupport, labelSupport, nextOpenDate, offerText,
    offersTicket, pinnedSupport, reasonText, ticketErrorText as supportErrorText,
    ticketReady as ticketIsReady,
} from '../../chatSupport.js';
/*
 * #2101 — office hours as the SERVER decides them, and the per-person chime and
 * flash. Both are separate modules for the reason `chatShapes.js` is: this
 * component cannot be loaded under node, and the mistakes live in the rules
 * (a timezone ignored, a chime for your own message, a title left flashing).
 *
 * `chatHours` is a line-for-line port of cs3-chat's OfficeHours /
 * OutsideHoursRules — NOT the preview's `isOutsideHours`, which reads the
 * browser's own clock and ignores the client's timezone. Its docblock says why
 * that could not be reused.
 */
import {
    allowedOutsideHours, clientClosedMessage, hoursStrip, isOpenNow, mayPostInHours, officeHours,
    // ⚠ ALIASED: `chatPreview.js` exports a `nextOpenLabel` too, and the two
    // are different functions — the preview's reads the browser's clock, this
    // one the client's timezone. Importing both under one name is a build
    // error; importing the wrong one would be a silent timezone bug.
    nextOpenLabel as nextOpenInClientTz,
} from '../../chatHours.js';
import {
    createChime, createTitleFlasher, readPrefs, shouldChime, shouldFlash, writePrefs,
} from '../../chatNotify.js';
import { mapGetters } from 'vuex';

const MAX_SCROLLBACK = 120;

/*
 * #2096 — stands in for "no channel at all", so the channel view cannot throw
 * on a live build whose roster is empty. Frozen: it is shared, and nothing
 * should ever write to it.
 */
const EMPTY_CHANNEL = Object.freeze({
    id: null, name: '', label: '', grp: 'client', kind: null,
    archived: false, unread: 0, last: '', at: '—',
});
const PAGE_STEP = 35;

/**
 * Which Configuration group a channel belongs to, from its label. "Skills by
 * Status" is tested BEFORE "Skills" — it starts with the same word, and the
 * looser test first would swallow it and tie the wrong switch to it.
 */
function groupKeyFor(channel) {
    const label = channel.label;
    if (label.indexOf('Job') === 0) return 'Job';
    if (label.indexOf('Employers / Payroll') === 0) return 'Employers / Payroll';

    return label;
}

const DOCK_KEY = 'cs3-chat-dock-state';
const POPOUT_NAME = 'cs3ChatWindow';
/** Same width as the docked panel, so undocking does not reflow the content. */
const POPOUT_FEATURES = 'width=400,height=900,resizable=yes,scrollbars=yes';

export default {
    name: 'chatPanel',
    props: {
        /**
         * 'docked'  — the panel on a CS3 page (default)
         * 'window'  — the pop-out, filling its own browser window
         * One component in two shapes rather than two components: the channel
         * list, the window rules, the composer and the 120-message scrollback
         * are the same chat either way, and a second copy would drift.
         */
        mode: { type: String, default: 'docked' },
    },
    data() {
        return {
            collapsed: true,
            view: 'list',
            openId: 'c7_skill_audio',
            shown: 50,
            draft: '',
            sent: {},
            /*
             * ⚠ #2096 — EMPTY IN A LIVE BUILD, AND THAT MATTERS.
             *
             * Seeding the preview's channels unconditionally put invented
             * people, an invented local name and invented message previews in
             * front of real users for as long as the handoff took — and
             * permanently if it FAILED, because nothing would ever replace
             * them. With the "Preview as" strip hidden in a live build there
             * was nothing on screen marking any of it as fake.
             */
            channels: chatLiveEnabled() ? [] : JSON.parse(JSON.stringify(CHANNELS)),
            generated: buildHistory(),
            roleLabel: ROLE_LABEL,
            rolePlural: ROLE_PLURAL,
            previewRoles: PREVIEW_ROLES,
            // #2091 r5 — 'r:<role>' or 'p:<eid>'. null means "whoever is signed
            // in", so the panel behaves exactly as before until it is touched.
            viewAs: null,
            toast: null,
            toastLeft: 0,
            toastTimer: null,
            // The preview has no real chat identity, so the panel reads the
            // signed-in CS3 role and shows the channels that role would see.
            settings: previewStore(),
            // #2091 r4 — undock state, remembered for the session so a reload of
            // the main page does not silently re-dock a chat the user moved to
            // another monitor.
            undocked: false,
            popout: null,
            popoutWatch: null,
            popupBlocked: false,
            // Re-evaluated each minute so the panel crosses an opening or
            // closing time while someone is looking at it, rather than only on
            // a reload.
            now: new Date(),
            clock: null,

            /*
             * #2096 — live state. All of it stays null/empty in a preview
             * build, and every computed below asks `isLive` first, so the
             * preview path is byte-for-byte what Ed reviewed for #2091.
             */
            /*
             * ⚠ ONLY THE PLAIN FACTS ABOUT THE SIGNED-IN USER LIVE HERE, NOT
             * THE SESSION OBJECT.
             *
             * Vue 2 walks everything returned from `data()` and makes it
             * reactive in place, recursively. The session carries the Firebase
             * app and the Firestore instance, so putting it here would have Vue
             * rewriting the SDK's own internals with reactive getters — slow at
             * best, and at worst it breaks the client it is instrumenting.
             *
             * So the session lives on the instance as `this.chat`, assigned
             * outside `data()` and therefore never observed, and the handful of
             * values the template actually needs are copied here.
             */
            me: null,
            // 'idle' | 'signing-in' | 'ready' | 'failed'
            liveState: 'idle',
            // A ChatUnavailable code, shown to the user rather than logged away.
            liveError: null,
            // channelId -> the messages Firestore last sent for it.
            liveMessages: {},
            /*
             * The MESSAGE listener for the channel currently open — one, not
             * one per channel. See `watchOpenChannel`.
             */
            openListener: null,
            openListenerFor: null,
            // One unsubscribe covering every channel's own Firestore document.
            docListener: null,
            // Posting is a round trip; the composer is disabled while it runs
            // so one Enter cannot become two messages.
            sending: false,

            /*
             * #2101 — the client's REAL chat settings, exactly as the API
             * returned them (`office_hours`, `outside_hours`, …).
             *
             * ⚠ THE API'S SHAPE, NOT `chatSettings.toScreen`'s. That translation
             * is for Configuration → Chat and is lossy twice over here: it
             * re-keys the days into a Sunday-first array, and it DROPS
             * `outside_hours.people` because the settings screen cannot resolve
             * a chat uid to a name. The panel does not need a name — it needs to
             * know whether its own uid is in that list — so it reads the shape
             * the server sent.
             *
             * Null until the fetch lands, and null for CS staff (who have no
             * client of their own and are never restricted by one's schedule).
             */
            liveSettings: null,
            // Sound/volume/flash, per person per browser. Read once on mount so
            // a storage accessor that throws does so in one place.
            notify: readPrefs(),
            notifyOpen: false,
            // The week opens on request: the strip has to be readable at a
            // glance in a 400px panel, and today's line is what matters most.
            hoursOpen: false,
            /*
             * #2110 — THE BADGE, FROM THE SERVER.
             *
             * `GET /api/me/unread`'s `total`, which is the launcher badge in a
             * live build. The per-channel counts are written onto the roster
             * rows (`channel.unread`) by `applyUnreadCounts`; nothing in this
             * component adds to either. A message that arrives is a reason to
             * ASK again (`refreshUnread`), not a number to add.
             *
             * `unreadLoaded` is the baseline flag: the first answer after a page
             * load makes rows bold and chimes for nothing (those messages are
             * not new, they are unread) — the same rule `primed` applied to the
             * Firestore documents before #2110, now applied where the count
             * actually comes from.
             */
            unreadServerTotal: 0,
            unreadLoaded: false,
            // True while the tab title is blinking, so the docked launcher can
            // blink with it and so opening the panel can stop both.
            flashing: false,

            /*
             * #2098 — the person search. `query` is what is typed; `people` is
             * what the SERVER returned for it, never a locally filtered copy —
             * Ed's who-can-be-found rule is applied by cs3-chat's DirectReach
             * and a browser-side filter could only ever disagree with it.
             */
            peopleQuery: '',
            people: [],
            // #2118 — the employer's contacts. `idle` until the panel asks, so
            // a role that gets no section never produces a request.
            contactGroups: [],
            contactsState: 'idle',
            contactsError: null,
            // 'idle' | 'typing' | 'searching' | 'done' | 'failed'
            peopleState: 'idle',
            peopleError: null,
            searchOpen: false,
            // The uid currently being opened, so one click cannot become two
            // conversations and the row can show it is working.
            opening: null,
            directError: null,
            /*
             * #2098 / cs3-chat #23 — uid → true for everyone we have been told
             * is online. A plain object with `$set`, because Vue 2 cannot see
             * new keys on one otherwise, and the dots appear as the Realtime
             * Database answers rather than all at once.
             *
             * ⚠ ABSENT MEANS NO DOT, and absent is the common case: the rules
             * answer PERMISSION_DENIED for a person who has never opened chat
             * (there is no node, so the `cid` comparison cannot match), and
             * most people in a local never have. `chatPresence` records that as
             * offline and says nothing — see its header.
             */
            online: {},
            // Names we have learned from the search, so a conversation can be
            // labelled even when the symmetric stored name cannot be split.
            knownNames: {},

            /*
             * #2102 — files picked but not yet sent, for the channel on screen.
             *
             * Each is `{ key, name, size, type, pct, state, error, id }` where
             * `state` is 'uploading' | 'ready' | 'failed' and `id` is the
             * server's attachment id once it has one. `key` is a local counter,
             * NOT the server id: a row has to be addressable while it is still
             * uploading and has no id yet, and two files can share a name.
             */
            pending: [],
            pendingKey: 0,
            dragging: false,
            attachError: null,
            /*
             * #2102 — object URLs for attachments already fetched, keyed by
             * attachment id, so scrolling back through a conversation does not
             * re-fetch every photo. Vue needs to see the keys appear, hence a
             * plain object with `$set`; the CACHE itself (which owns the blobs
             * and must revoke them) lives on `this.files`, off `data()`.
             */
            thumbs: {},
            // id -> true while its bytes are in flight, so two renders of the
            // same thumbnail do not both fetch it.
            fetching: {},
            // The photo opened larger, or null.
            lightbox: null,

            /*
             * #2108 — CallSteward Support.
             *
             * `support` is the last availability answer, shaped by
             * `chatSupport.normaliseAvailability`, or null before one has been
             * asked for. It is asked on OPENING the conversation and not on
             * sign-in: it is a question about this minute (is anyone at the
             * desk), and asking it on every page load would spend a request
             * per page for a panel most people do not open.
             */
            support: null,
            supportChecking: false,
            ticketDraft: '',
            ticketSending: false,
            // A ChatUnavailable code from `submitSupportTicket`, or null.
            ticketError: null,
            // The FreeScout ticket NUMBER once one is open, so the panel can
            // show something the person can quote. Cleared by `newTicket()`.
            supportTicket: null,
        };
    },
    computed: {
        ...mapGetters({ userRoleSlug: 'userRoleSlug' }),
        /** #2096 — is this panel talking to the real chat service? */
        isLive() { return chatLiveEnabled(); },

        /**
         * The flag builds it in; the CA's switch turns it on. Chat off means the
         * panel and its launcher are GONE — not greyed out — which is the
         * wireframe's "chat off" state, reachable from Configuration -> Chat.
         *
         * ⚠ #2096 — IN A LIVE BUILD THE SWITCH IS THE SERVER'S ANSWER, not the
         * local preview store. `chat_visible` comes back resolved on the
         * handoff (cs3-chat HandoffExchange) because the client must not be the
         * one deciding whether a client's chat is on; the Firestore rules
         * enforce the same thing on reads, so a browser that ignored this would
         * see an empty panel rather than someone else's messages.
         *
         * While the handoff is still in flight the panel IS shown: it has a
         * signing-in state to display, and flashing the launcher away and back
         * on every page load is worse than a moment of "connecting".
         */
        visible() {
            if (!chatUiEnabled()) return false;
            if (!this.isLive) return this.settings.chatOn;
            if (this.liveState === 'ready') return !!(this.me && this.me.chatVisible);

            return true;
        },
        windowMode() { return this.mode === 'window'; },
        panelIsOpen() {
            // Undocked means the chat is NOT on this page, so the page gets its
            // full width back — the whole point of moving it to a second screen.
            return this.visible && !this.windowMode && !this.undocked && !this.collapsed;
        },

        /**
         * #2091 r3 — the job channel's 24h-either-side window, or null.
         *
         * #2096: this works on a live build. `ChannelAdmin::present()` sends
         * `starts_at`, `ends_at` and `own_option_on`, and `normaliseChannel`
         * shapes them into `job` — so the "Chat for this job opens Tue 08:00"
         * banner is the real window. The server still refuses an out-of-window
         * post with a 403; this is what puts the reason on screen first.
         */
        jobWindow() {
            return this.channel && this.channel.job ? jobChatWindow(this.channel.job, this.now) : null;
        },
        jobWindowLabel() { return windowLabel(this.jobWindow); },
        outside() {
            // #2101 — a live build asks the SERVER's schedule, in the CLIENT's
            // timezone. The preview keeps its own browser-local version.
            if (this.isLive) return !isOpenNow(this.liveHours, this.now);

            return isOutsideHours(this.settings.hours, this.now);
        },
        reopensAt() {
            if (this.isLive) return nextOpenInClientTz(this.liveHours, this.now);

            return nextOpenLabel(this.settings.hours, this.now);
        },
        /** #2101 — the client's office hours, server shape, or "always open". */
        liveHours() { return officeHours((this.liveSettings || {}).office_hours); },
        /**
         * #2101 item 1 — the strip at the top of the chat box.
         *
         * ⚠ ALWAYS VISIBLE IN A LIVE BUILD, including when the client has no
         * schedule at all: "Open 24 hours" is a fact a crew wants as much as a
         * closing time, and a strip that appeared only on closed days would
         * read as the panel breaking. Null only for CS staff and while the
         * settings fetch is still out — there is nothing truthful to show then.
         */
        hours() {
            if (!this.isLive || !this.liveSettings) return null;

            return hoursStrip(this.liveSettings, this.now);
        },
        /**
         * Does the hours strip apply to THIS viewer's posting?
         *
         * CS staff are never restricted by a client's preferences, and a role
         * the CA exempted is not either — so the strip still shows the hours
         * (they are the client's hours either way) but drops the "you are
         * read-only" colouring, which would be a lie.
         */
        hoursBindMe() {
            if (!this.isLive) return true;
            if (this.isStaff) return false;

            return !allowedOutsideHours(
                (this.liveSettings || {}).outside_hours,
                this.me ? this.me.uid : '',
                this.role,
            );
        },
        mayAdmin() { return canAdminChat(this.userRoleSlug); },
        /**
         * The signed-in CS3 role slug -> the chat role they would really see.
         *
         * ⚠ #2096 — IN A LIVE BUILD THE ROLE COMES FROM THE SESSION. The slug
         * map below is a guess made in the browser; the live role was decided
         * by `App\Chat\ChatRole` from a SIGNED claim and is the same value the
         * chat service recorded and the Firestore rules will enforce. Letting
         * the browser's guess win would show a channel list that disagrees with
         * what the user can actually read.
         */
        realRole() {
            if (this.isLive && this.me && this.me.role) {
                return this.me.role;
            }

            return {
                'client-admin': 'ca',
                'super-admin': 'cs_admin',
                'power-user': 'pu',
                'power-user-minus': 'pu',
                'steward': 'steward',
            }[this.userRoleSlug] || 'member';
        },
        /** #2091 r5 — the named people offered in the Preview-as picker. */
        namedPeople() { return this.settings.hours.people; },
        /** The named person being previewed, or null when previewing a role. */
        asPerson() {
            // #2096 — there are no previewed people in a live build.
            if (this.isLive) return null;
            if (!this.viewAs || this.viewAs.slice(0, 2) !== 'p:') return null;
            const eid = Number(this.viewAs.slice(2));

            return this.namedPeople.find((x) => x.eid === eid) || null;
        },
        /**
         * The chat role in force. A previewed PERSON brings their own role with
         * them, so picking Dana Whitfield shows what a Steward sees.
         */
        role() {
            /*
             * ⚠ #2096 — A LIVE BUILD IGNORES `viewAs` ENTIRELY.
             *
             * Not just because the picker is hidden: `mounted` seeds `viewAs`
             * from `realRole`, and at mount the session has not arrived, so the
             * seed is the slug-map guess ('member' for most people). Honouring
             * it afterwards would pin the panel to that guess even once the
             * server had said 'ca' — a Client Admin would silently see a
             * Member's channel list for the rest of the session.
             */
            if (this.isLive) return this.realRole;

            if (this.asPerson) return this.asPerson.role;
            if (this.viewAs && this.viewAs.slice(0, 2) === 'r:') return this.viewAs.slice(2);

            return this.realRole;
        },
        viewAsNote() {
            if (this.asPerson) {
                return 'named individually — overrides the ' + (ROLE_PLURAL[this.asPerson.role] || 'role') + ' switch';
            }
            if (this.role === this.realRole) return 'your own role';

            return 'preview only — you are signed in as ' + (ROLE_PLURAL[this.realRole] || this.realRole);
        },
        isStaff() { return this.role === 'cs_admin' || this.role === 'cs_support'; },
        granted() { return this.role === 'ca' || this.role === 'pu'; },
        visibleChannels() {
            /*
             * ⚠ #2096 — IN A LIVE BUILD THERE IS NOTHING TO FILTER HERE.
             *
             * Every rule the preview re-implements below — role visibility,
             * the group switches, the job window, the two master switches, the
             * global grant — is applied by the chat service when it builds the
             * list (`ChatPolicy`), and again by the Firestore rules on read. A
             * second copy of those rules in the browser could only ever
             * DISAGREE with the authority, and the direction it disagrees in is
             * not knowable from here: hiding a channel the user may read looks
             * like data loss, and showing one they may not is a listener that
             * fails silently and bills reads while it retries.
             *
             * So the live list is the server's list, unfiltered. The preview
             * keeps its filters because making those switches visibly do
             * something is the whole point of #2091.
             */
            if (this.isLive) return this.channels;

            return this.channels.filter((c) => {
                if (c.see.indexOf(this.role) < 0) return false;
                // A group switched off in Configuration -> Chat takes its
                // channels out of the list, which is how that switch is seen to
                // do something.
                if (c.grp === 'client' && this.settings.groups[groupKeyFor(c)] === false) return false;

                // #2091 r3 — Job and Employers/Payroll are MASTER switches. The
                // client switch only PERMITS the channel; the job's or
                // employer's own Chat option is the other half, and a channel
                // exists only where both are on.
                if (c.job && !c.job.chatOn) return false;
                if (c.employer && !c.employer.chatOn) return false;
                if (c.grp === 'global' && c.needsGrant && !this.isStaff && this.role !== 'ca' && !this.granted) return false;

                return true;
            });
        },
        groupsShown() {
            /*
             * #2096 — the client group's heading is the PREVIEW's invented local
             * name ('Harbourview Stage Local'), which must not appear above a
             * real client's channels. The live heading is the client's own
             * name; until the chat service sends one it is a neutral label
             * rather than someone else's.
             */
            let groups = this.isLive
                ? GROUPS.map((g) => (g.key === 'client' ? { ...g, title: this.liveClientTitle } : g))
                : GROUPS;

            /*
             * #2098 — direct conversations get their own heading, after the
             * client's channels and before the CS-world ones.
             *
             * ⚠ A GROUP OF ITS OWN RATHER THAN MIXED IN WITH THE CLIENT'S
             * CHANNELS, even though a direct channel IS client-scoped. A
             * conversation is a person, not a channel: it has no `#`, its
             * membership is two people rather than a roster, and nobody
             * switches it on in Configuration → Chat. Listing it under the
             * local's name alongside "All Members" would read as a channel
             * everyone can see.
             *
             * Live only: the preview has no direct messages, and `groupsShown`
             * drops empty groups anyway, so this adds nothing there.
             */
            if (this.isLive) {
                groups = groups.slice(0, 2)
                    .concat([{ key: 'direct', title: 'Direct messages' }])
                    .concat(groups.slice(2));
            }

            return groups
                .map((g) => ({ ...g, rows: this.visibleChannels.filter((c) => c.grp === g.key) }))
                .filter((g) => g.rows.length);
        },

        /*
         * ── #2108 — CallSteward Support ──────────────────────────────────────
         */

        /**
         * The row pinned at the bottom of the list, or null.
         *
         * ⚠ MATCHED ON THE OWNER, NOT JUST THE KIND. A support agent's roster
         * contains EVERY support conversation in the silo (they read them all),
         * so picking "the first support channel" would pin somebody else's
         * conversation into their panel. `pinnedSupport` answers null for staff
         * and for a roster that has not arrived yet.
         */
        supportPinned() {
            if (!this.isLive) return null;

            return pinnedSupport(this.channels, this.me ? this.me.uid : '', this.isStaff);
        },
        /** Is the conversation on screen the support one? */
        inSupport() {
            return this.isLive && isSupport(this.channel);
        },
        /** Does the panel offer a ticket instead of the composer? */
        supportOffersTicket() {
            return offersTicket(this.support);
        },
        /**
         * Does Support replace the composer with one of its own states?
         *
         * The three that do: the availability request is still out, the desk is
         * shut (so a ticket is the route), or a ticket has just been opened.
         * Everything else — including `not_configured` and a failed check —
         * falls through to the ordinary composer, which is what keeps the
         * paperclip and the paste handler in this conversation.
         */
        supportPanel() {
            return this.inSupport
                && (this.supportChecking || this.supportOffersTicket || !!this.supportTicket);
        },
        /**
         * Why support is not live, in the server's words where it sent them.
         *
         * ⚠ THE FALLBACK IS THIS REASON'S OWN SENTENCE, not a generic one. It
         * said `reasonText('unknown')` first, which printed "we could not
         * check" over a perfectly good `not_configured` answer whose `message`
         * happened to be empty — a state that mislabels itself is worse than
         * one with no label. `normaliseAvailability` fills the message in, so
         * this is belt and braces; the screenshots are what showed it.
         */
        supportReason() {
            const a = this.support || {};

            return a.message || reasonText(a.reason || 'unknown');
        },
        /** What the person can do about it. */
        supportOffer() {
            return offerText(this.support);
        },
        /**
         * The one-line note above a LIVE support composer.
         *
         * Empty when support is plainly open: a conversation that works needs
         * no explanation. It carries the two states that fall through to the
         * composer without being `open` — `not_configured` (no FreeScout on
         * this deployment, so there is nothing to ticket into) and `unknown`
         * (the availability request itself failed) — because in both the person
         * should know that nobody has promised them an agent is reading.
         */
        supportNote() {
            const reason = (this.support || {}).reason;
            if (!reason || reason === 'open') return '';

            return this.supportReason + ' ' + this.supportOffer;
        },
        /**
         * Can this person open a ticket at all?
         *
         * ⚠ ASKED BEFORE THE MESSAGE IS WRITTEN. cs3-chat creates the ticket
         * FOR the email address on the person's row and refuses with a 422 when
         * there is none (issue item 4), so the form says what is missing up
         * front rather than after someone has typed out their problem. The
         * 422 is still handled — the address could have been removed since
         * sign-in — but it should not be how people find out.
         */
        canTicket() {
            return !!(this.me && this.me.hasEmail);
        },
        /**
         * When the desk reopens, as a Date.
         *
         * ⚠ PARSED IN `chatSupport`, NOT HERE. `shortWhen` calls `getDay()`, so
         * the raw DATE_ATOM string FreeScout sends threw inside the render and
         * blanked the panel — found by rendering it, not by reading it. Null
         * when absent or unparseable, and the line is hidden on null.
         */
        supportNextOpen() { return nextOpenDate(this.support); },
        noEmailText() { return supportErrorText('ticket_no_email'); },
        maxTicketChars() { return MAX_TICKET_CHARS; },
        ticketReady() { return ticketIsReady(this.ticketDraft); },
        ticketErrorText() { return supportErrorText(this.ticketError); },

        /**
         * #2098 — the conversation on screen, when it is a direct one.
         *
         * Null for every other kind, so the header's dot and the
         * "no longer reachable" note have one thing to ask.
         */
        openDirectChannel() {
            return (this.channel && this.channel.kind === 'direct') ? this.channel : null;
        },
        /** Is the person in the open conversation online? */
        openDirectOnline() {
            const c = this.openDirectChannel;

            return !!(c && c.otherUid && this.online[c.otherUid] === true);
        },
        /**
         * Who to watch for presence: everyone on screen, and nobody else.
         *
         * ⚠ BOTH THE SEARCH RESULTS AND THE CONVERSATION LIST. The dots in the
         * channel list have to be right before the panel is opened — that is
         * what a dot beside a conversation is for — so this cannot be narrowed
         * to the list currently displayed.
         *
         * ⚠ AND NOTHING ELSE. One listener per uid is one Realtime Database
         * subscription, and a search box that attached them for every name it
         * had ever shown would accumulate them for the life of the tab.
         * `chatPresence.diffWatch` turns this into the adds and removes.
         */
        presenceUids() {
            const uids = this.people.map((p) => p.uid);

            this.visibleChannels.forEach((c) => {
                if (c.kind === 'direct' && c.otherUid) uids.push(c.otherUid);
            });

            return uids.filter((uid, i) => uid && uids.indexOf(uid) === i);
        },
        /**
         * #2102 — may this person attach a file here, right now?
         *
         * ⚠ EXACTLY `mayPost`, PLUS LIVE. Issue item 4: "read-only means no
         * paperclip". An attachment IS part of a message, so every reason the
         * composer is closed — outside office hours for a Member, a job channel
         * outside its window, an archived channel, a conversation whose other
         * person has gone — is a reason there is no paperclip. The server
         * refuses the upload on the same rule (cs3-chat #25 checks
         * `refusalToPost` at upload time), so this only decides whether to
         * OFFER it.
         */
        mayAttach() {
            return this.isLive && this.mayPost && !this.channel.archived;
        },
        /** Uploads that finished and have a server id, in the order picked. */
        readyAttachments() {
            return this.pending.filter((p) => p.state === 'ready' && p.id);
        },
        /** Is anything still going up? The Send button waits for it. */
        uploading() {
            return this.pending.some((p) => p.state === 'uploading');
        },
        attachAccept() { return ACCEPT_ATTR; },
        maxAttachments() { return MAX_PER_MESSAGE; },
        /**
         * The ids of every thumbnail-able attachment on screen, as one string.
         *
         * A watch key rather than a list: it changes when the visible photos
         * change and not when anything else about the conversation does.
         */
        visibleAttachmentIds() {
            if (!this.isLive || this.view !== 'channel') return '';

            const ids = [];
            this.slice.forEach((m) => {
                (m.attachments || []).forEach((a) => { if (a.thumbnail) ids.push(a.id); });
            });

            return ids.join(',');
        },

        /** #2098 — one sentence for whatever stopped a conversation opening. */
        directErrorText() {
            return {
                direct_not_allowed: 'You can no longer message this person — they have left your '
                    + 'local, or the group you share has been switched off.',
                direct_no_such_person: 'That person is no longer in chat.',
                direct_not_possible: 'A conversation cannot be opened with that person.',
                chat_session_expired: 'Your chat session has expired. Reload the page.',
            }[this.directError] || 'Chat is unavailable. Try again in a moment.';
        },
        /** #2098 — and for a search that failed. */
        peopleErrorText() {
            return {
                chat_session_expired: 'Your chat session has expired. Reload the page.',
                chat_not_permitted: 'You cannot search people in this client.',
                chat_search_rejected: 'That search was refused.',
            }[this.peopleError] || 'Search is unavailable. Try again in a moment.';
        },
        minQuery() { return MIN_QUERY; },

        /**
         * #2118 — does this role get a "Your contacts" section at all?
         *
         * ⚠ THE ROLE, NOT THE LIST. A Member and an employer with no
         * switched-on client both get `[]` from the server; deciding on the
         * array would give every Member in the fleet an empty heading. The rule
         * lives in `chatContacts.showsContacts` so it is tested rather than
         * spelled out in a template.
         */
        showsContacts() {
            /*
             * ⚠ `realRole`, NOT `role`. `role` can be the CA's preview
             * override (`asPerson`), and this section is backed by a SERVER
             * call authorised by the session: previewing "as an employer"
             * would draw a heading the server answers `[]` for, and previewing
             * "as a member" must not hide an employer's own contacts. The
             * section has to describe who is actually signed in.
             */
            return showsContacts(this.realRole);
        },

        contactsErrorText() {
            return {
                chat_session_expired: 'Your chat session has expired. Reload the page.',
                chat_not_permitted: 'You cannot see contacts in this client.',
            }[this.contactsError] || 'Your contacts are unavailable. Try again in a moment.';
        },

        /** The heading over a live build's client channels. */
        liveClientTitle() {
            return (this.me && this.me.clientName) || 'Your local';
        },
        /**
         * The launcher badge. Live: the server's total — the same number the
         * phone shows (#2110). Preview: the invented rows' own counts.
         */
        unreadTotal() {
            if (this.isLive) return this.unreadServerTotal;

            return this.visibleChannels.reduce((n, c) => n + c.unread, 0);
        },
        /*
         * #2096 — can be undefined when a live roster is empty (chat on, no
         * channels yet). `view` only advances through `openChannel`, so the
         * channel branch of the template is unreachable in that state today —
         * but it is one empty list away from being reachable, and the template
         * reads `channel.archived` and `channel.name` unguarded. Falling back
         * to an inert placeholder keeps that a blank panel rather than a
         * render error that takes the page with it.
         */
        channel() {
            return this.channels.find((c) => c.id === this.openId)
                || this.visibleChannels[0]
                || EMPTY_CHANNEL;
        },
        history() {
            // #2096 — live messages come from Firestore, already oldest-first
            // and already shaped by chatService.normaliseMessage.
            if (this.isLive) {
                return (this.channel && this.liveMessages[this.channel.id]) || [];
            }

            const me = { name: 'You', initials: 'ME', role: this.role, colour: '#3f6bbf' };

            return historyFor(this.channel, this.generated, me).concat(this.sent[this.channel.id] || []);
        },
        slice() { return this.history.slice(Math.max(0, this.history.length - this.shown)); },
        shownCount() { return Math.min(this.shown, this.history.length); },
        atCap() { return this.shown >= MAX_SCROLLBACK; },
        headTitle() {
            if (this.view === 'list') return 'Chat';

            /*
             * ⚠ #2098 — NO `#`, AND NOT `channel.name`, FOR A CONVERSATION.
             * The stored name of a direct channel is symmetric ("Ada Lovelace &
             * Alan Turing", cs3-chat `DirectMessages::name`) because one string
             * serves both people, so `'#' + name` would head the conversation
             * with both names and a hash. `label` is the other person, resolved
             * by `chatPeople.labelDirect`.
             */
            /*
             * ⚠ #2108 — AND NO `#` FOR SUPPORT EITHER. "#CallSteward Support"
             * reads as a channel in the client's list, which is the one thing
             * this conversation is not: it is with CallSteward, it has one
             * member, and nobody switched it on. Same reasoning as the direct
             * case above, and the screenshots are what made it obvious.
             */
            return this.hashless ? this.channel.label : '#' + this.channel.name;
        },
        /**
         * #2098 — what the composer says it is messaging: "#all-members" for a
         * channel, a person's name for a conversation. One computed, because
         * the placeholder, the aria-label and the two closed-state stand-ins
         * all have to agree.
         */
        composeTarget() {
            if (!this.channel || !this.channel.id) return '';

            return this.hashless ? this.channel.label : '#' + this.channel.name;
        },
        /**
         * Which conversations are NOT written with a `#`.
         *
         * A direct message (#2098) and the support conversation (#2108): both
         * are people rather than channels, neither appears in the client's
         * channel list, and both carry a `label` that is already the right
         * thing to print. One predicate so the header, the composer
         * placeholder, the aria-label and the read-only stand-ins cannot
         * disagree about it.
         */
        hashless() {
            return !!this.openDirectChannel || this.inSupport;
        },
        headSub() {
            if (this.view === 'list') {
                /*
                 * #2096 — in a live build this line is the connection state,
                 * because "why is this empty" is the question the user will
                 * have and the answer is never the fake client's name.
                 */
                if (this.isLive) {
                    if (this.liveState === 'signing-in') return 'Connecting…';
                    if (this.liveState === 'failed') return this.liveErrorText;

                    return (this.me && this.me.name) || 'Chat';
                }

                return 'Harbourview Stage Local · preview';
            }
            // A job channel's header carries its window, so the reason it is or
            // is not chatable is on screen rather than inferred.
            if (this.jobWindowLabel) return this.jobWindowLabel;

            return this.channel.label + (this.channel.archived ? ' · archived' : '');
        },

        /**
         * #2096 — a ChatUnavailable code turned into something a dispatcher can
         * act on.
         *
         * Every one of these is a real state the chat service can put the panel
         * in, and they want different responses from the user: wait, reload,
         * shorten the message, or tell the office. A single "chat error" would
         * collapse all of that — and the service's own 401 is deliberately
         * indistinguishable between causes, so this is the only layer that can
         * say anything useful at all.
         */
        liveErrorText() {
            return {
                no_logged_in_client: 'Chat needs a local selected. Pick one and reload.',
                handoff_rejected: 'Chat sign-in expired. Reload the page to try again.',
                chat_session_expired: 'Chat sign-in expired. Reload the page to try again.',
                chat_unreachable: 'Chat is unreachable right now.',
                chat_unavailable: 'Chat is not configured on this server.',
                cs3_handoff_incomplete: 'Chat is not configured on this server.',
                chat_session_missing: 'Chat is not configured on this server.',
                chat_listen_failed: 'Lost the connection to chat. Reload the page.',
                // The UUID/integer contract gap — see ChatHandoff::cid. Worded
                // for the person who will see it on stage- before that is
                // settled, so it does not read as a network fault.
                chat_client_not_addressable: 'Chat cannot address this local yet. Tell the office.',
                // The chat box is running a build older than cs3-chat #24, so
                // the roster endpoint is not there. A deploy, not a network
                // fault — worth saying, or someone checks DNS.
                chat_roster_unavailable: 'This server’s chat needs updating. Tell the office.',
                chat_not_permitted: 'You do not have access to chat for this local.',
                // Not shown in practice: the panel that would display it is
                // being torn down. Here so the code is never a bare fallback.
                chat_sign_in_abandoned: 'Chat sign-in was interrupted.',
                posting_closed: 'Posting is closed in this channel.',
                message_too_long: 'That is over the 4,000-character limit.',
                message_rejected: 'That message was refused.',
                too_many_messages: 'Too many messages just now — wait a moment.',
            }[this.liveError] || 'Chat is unavailable.';
        },
        counterClass() {
            if (this.draft.length >= 4000) return 'cp__over';

            return this.draft.length >= 3600 ? 'cp__warn' : '';
        },
        /**
         * Precedence: person beats role beats schedule, exactly as the wireframe
         * and spec row 21 have it. CS staff are never restricted.
         */
        mayPost() {
            /*
             * ⚠ #2108 ITEM 3 — THE CLIENT'S OFFICE HOURS NEVER CLOSE SUPPORT.
             *
             * Ed's rule from cs3-chat #27: "a client's office hours limit only
             * employees talking to people inside their own client, never
             * Support." The server says the same thing in one place
             * (`ChannelKind::boundByClientHours()`), and this is its mirror —
             * so the read-only banner and the locked composer below skip this
             * conversation, and #2106's client-defined closed message will skip
             * it too by asking the same question rather than adding its own.
             *
             * Checked FIRST, before the archived and job-window rules, because
             * neither can apply to it: nothing archives a support conversation
             * (cs3-chat refuses `canDefineChannel` for the kind) and it is not
             * a job.
             */
            if (this.inSupport) return true;

            /*
             * An archived channel and a job window are decided from the channel
             * the server sent, and are checked first — `ChatPolicy::refusalToPost`
             * orders it the same way, so that a member in a closed job channel
             * is told the job is closed rather than told to come back at 09:00.
             */
            if (this.jobWindow && this.jobWindow.state !== 'open') return false;

            /*
             * ⚠ #2101 — THE LIVE BUILD NOW DECIDES THIS, AND IT WAS `return
             * true` BEFORE.
             *
             * #2096 left it open deliberately: the schedule lived on the chat
             * service and the panel had no way to read it, so the only honest
             * thing was to let the server refuse the post with a 403. The cost
             * was that a basic employee found out by typing a message and having
             * it rejected — which is the complaint behind this issue.
             *
             * The settings are now fetched (`startLive`), and `mayPostInHours`
             * is a port of `OfficeHours` + `OutsideHoursRules` that agrees with
             * the server on 1,064 generated cases including both DST
             * boundaries. It applies the same way in group channels, job
             * channels and direct messages, because the schedule belongs to the
             * CLIENT and not to a kind of channel.
             *
             * ⚠ THE SERVER IS STILL THE AUTHORITY. This closes the composer
             * early; it does not grant anything. A post the panel allows and the
             * server refuses still comes back 403 and `sendLive` still shows it
             * — and while the settings fetch is out, or if it failed, the
             * composer stays OPEN rather than guessing, exactly as before.
             */
            if (this.isLive) {
                if (!this.liveSettings) return true;

                return mayPostInHours(
                    this.liveSettings,
                    this.me ? this.me.uid : '',
                    this.role,
                    this.now,
                );
            }

            if (!this.outside) return true;
            if (this.isStaff) return true;

            // #2091 r5 — PERSON BEATS ROLE, and it is now the previewed PERSON.
            // This used to look up a named person by matching their ROLE against
            // the viewed role, because the panel had no identity to match on. The
            // screen claims "person, then role, then the schedule", and that
            // approximation contradicted it out loud: Dana Whitfield is named
            // allowed and is a Steward, so with the Stewards switch OFF every
            // steward could still post and the switch looked broken.
            if (this.asPerson) return this.asPerson.allowed;

            return this.settings.hours.roles[this.role] === true;
        },
        closedReason() {
            /*
             * #2101 — in a live build the panel knows which switch closed the
             * composer, and says so. It names the ROLE rather than the person:
             * `outside_hours.people` is keyed by chat uid with no name beside
             * it, so "you are named individually" is all that can truthfully be
             * said — and that case cannot reach here, because a person named
             * `true` may post and a person named `false` is told it is their own
             * exception.
             */
            if (this.isLive) {
                const people = ((this.liveSettings || {}).outside_hours || {}).people || {};
                const uid = this.me ? this.me.uid : '';
                if (uid && Object.prototype.hasOwnProperty.call(people, uid)) {
                    return 'Your Client Admin has set you individually as not allowed to post '
                        + 'outside office hours.';
                }

                return 'Your Client Admin has not allowed ' + (ROLE_PLURAL[this.role] || 'people in your role')
                    + ' to post outside office hours.';
            }

            if (this.asPerson) {
                return 'Your Client Admin has not allowed ' + this.asPerson.name
                    + ' to post outside office hours.';
            }

            return 'Your Client Admin has not allowed ' + (ROLE_PLURAL[this.role] || 'people in your role')
                + ' to post outside office hours.';
        },
        /**
         * #2106 — the client's own closed message, or '' when the CA wrote none.
         *
         * ⚠ '' IS NOT "THE SERVER SENT NOTHING". cs3-chat #28 resolves the
         * default server-side (`presentHours` → `displayMessage()`), so a
         * Member's read always carries a sentence; `clientClosedMessage` is the
         * port that tells the CA's wording from our fallback. See it there.
         *
         * The preview keeps its own copy of the field, so Configuration → Chat
         * and the panel demonstrate the feature together against invented data.
         */
        clientMessage() {
            if (!this.isLive) return (this.settings.hours.closedMessage || '').trim();

            return clientClosedMessage(this.liveSettings);
        },
        /**
         * What the read-only block actually says.
         *
         * The client's words when there are any; otherwise `closedReason`,
         * which names the switch that closed the composer. That is the default
         * wording the issue asks for, and it is a better default than the stock
         * sentence: the heading above already says "read-only outside office
         * hours", so repeating it would cost the reader the one line that tells
         * them whether to ask their CA or just come back at 09:00.
         */
        closedText() {
            return this.clientMessage || this.closedReason;
        },
    },
    methods: {
        shortWhen,

        /** Open the chat in its own window, and stand down the docked panel. */
        undock() {
            this.popupBlocked = false;
            const url = window.location.origin + window.location.pathname + '#/chat-window';
            const win = window.open(url, POPOUT_NAME, POPOUT_FEATURES);

            // A blocked pop-up returns null (or a window that is already closed).
            // Say so and stay docked rather than leaving the user with a chat
            // that silently went nowhere.
            if (!win || win.closed) {
                this.popupBlocked = true;

                return;
            }

            this.popout = win;
            this.setUndocked(true);
            this.watchPopout();
            try { win.focus(); } catch (e) { /* focus is best effort */ }
        },

        /** Bring it back: close the pop-out if we still have a handle on it. */
        dock() {
            if (this.popout && !this.popout.closed) {
                try { this.popout.close(); } catch (e) { /* it may be gone already */ }
            }
            this.popout = null;
            this.stopWatchingPopout();
            this.setUndocked(false);
            this.collapsed = false;
        },

        /** Dock pressed INSIDE the pop-out: tell the opener, then close. */
        dockFromWindow() {
            try {
                if (window.opener && !window.opener.closed) {
                    window.opener.postMessage({ type: 'cs3-chat-dock' }, window.location.origin);
                }
            } catch (e) { /* opener gone or cross-origin; closing still re-docks */ }
            window.close();
        },

        setUndocked(value) {
            this.undocked = value;
            try {
                window.sessionStorage.setItem(DOCK_KEY, value ? 'undocked' : 'docked');
            } catch (e) { /* private mode: the state just does not survive a reload */ }
        },

        /**
         * Closing the pop-out by its own X must re-dock too, and a window has no
         * event the opener can listen to for that — so poll `closed`. Cheap, and
         * the only thing that works across a user-closed window.
         */
        watchPopout() {
            this.stopWatchingPopout();
            this.popoutWatch = setInterval(() => {
                if (!this.popout || this.popout.closed) {
                    this.popout = null;
                    this.stopWatchingPopout();
                    this.setUndocked(false);
                }
            }, 500);
        },

        stopWatchingPopout() {
            if (this.popoutWatch) {
                clearInterval(this.popoutWatch);
                this.popoutWatch = null;
            }
        },

        onDockMessage(event) {
            // Same-origin only, and only our own message shape.
            if (event.origin !== window.location.origin) return;
            if (!event.data || event.data.type !== 'cs3-chat-dock') return;
            this.dock();
        },
        dayOf(i) { return i < 0 ? null : (this.slice[i] || {}).day; },
        isCont(i) {
            const prev = this.slice[i - 1];

            return !!prev && prev.who.n === this.slice[i].who.n && prev.day === this.slice[i].day;
        },
        openChannel(id) {
            this.openId = id;
            this.view = 'channel';
            this.shown = 50;
            this.draft = '';
            // #2101 — "blink until the chat is opened or focused". Opening a
            // channel is the strongest form of opened.
            this.stopFlash();
            /*
             * #2110 — in a live build the count is NOT zeroed here. Opening the
             * conversation sends the read mark (`syncReading`, via the `openId`
             * watcher) and the server's answer zeroes the row; if that request
             * fails the count stays, which is the truth. The preview has no
             * server, so it keeps clearing its invented rows.
             */
            if (!this.isLive) {
                const c = this.channels.find((x) => x.id === id);
                if (c) c.unread = 0;
            }
            // #2096 — start reading this channel. Opening one channel does not
            // stop the others: the channel-list previews and the toast both
            // depend on messages arriving for channels nobody is looking at.
            if (this.isLive) this.watchOpenChannel();

            /*
             * #2108 — "On opening, call GET /api/support/availability" (issue
             * item 2). Asked here rather than on sign-in because the answer is
             * about this minute — whether anybody is at the desk — and because
             * a request per page load for a panel most people never open is a
             * cost with nothing to show for it.
             *
             * The draft and the last ticket number are cleared on every open so
             * one person's half-written ticket cannot reappear under a later
             * visit, and so "Ticket #1042 is open" does not greet somebody who
             * came back an hour later with a new problem.
             */
            if (this.inSupport) {
                this.ticketDraft = '';
                this.ticketError = null;
                this.supportTicket = null;
                this.checkSupport();
            }

            this.$nextTick(() => this.scrollToNewest());
        },
        loadOlder() {
            this.shown = Math.min(MAX_SCROLLBACK, this.shown + PAGE_STEP);
            /*
             * #2096 — scrollback in a live build means a WIDER query, not a
             * slice of something already held: Firestore only sent `shown`
             * documents. Re-listening replaces the listener for this channel
             * (watchChannel detaches the old one first), so the window grows
             * without accumulating listeners.
             *
             * The ceiling is the chat service's, not a UI preference — the
             * Firestore rules refuse any single query over 120 (spec decision
             * #14), and anything older is recovered from the chat database by a
             * CA or PU through Reports.
             */
            if (this.isLive) this.watchOpenChannel();
            // Land on what just loaded, which at the ceiling is the Reports note.
            this.$nextTick(() => { if (this.$refs.body) this.$refs.body.scrollTop = 0; });
        },
        scrollToNewest() {
            if (this.$refs.body) this.$refs.body.scrollTop = this.$refs.body.scrollHeight;
        },
        send() {
            const text = this.draft.trim();
            /*
             * ⚠ #2102 — A PHOTO WITH NO CAPTION IS A MESSAGE. The guard used to
             * be `if (!text) return`, which would have made the Send button
             * inert for exactly the case the paperclip exists for.
             */
            if (!text && !this.readyAttachments.length) return;

            // #2096 — a live build posts to the chat service.
            if (this.isLive) {
                this.sendLive(text);

                return;
            }

            const list = this.sent[this.channel.id] || [];
            // Local only. There is nowhere to send it to, and that is the point.
            list.push({
                who: { n: 'You', i: 'ME', r: this.role, c: '#3f6bbf' },
                day: TODAY,
                at: new Date().toTimeString().slice(0, 5),
                body: text,
            });
            this.$set(this.sent, this.channel.id, list);
            this.draft = '';
            this.$nextTick(() => this.scrollToNewest());
        },

        // ── #2096: the live path ────────────────────────────────────────────

        /**
         * Sign in to chat and load the channel list.
         *
         * ⚠ NOTHING HERE THROWS PAST THIS METHOD. Chat is a panel beside the
         * app, not the app: a chat server that is down, misconfigured or
         * unreachable must leave dispatch entirely usable. Every failure
         * becomes `liveState = 'failed'` plus a line the user can read.
         */
        async startLive() {
            if (this.liveState === 'signing-in' || this.liveState === 'ready') return;

            this.liveState = 'signing-in';
            this.liveError = null;

            try {
                const session = await signIn();
                // Deliberately NOT in `data()` — see the `me` comment there.
                this.chat = session;
                this.me = {
                    uid: session.uid,
                    role: session.role,
                    cid: session.cid,
                    name: session.name,
                    clientName: session.clientName,
                    chatVisible: session.chatVisible,
                    /*
                     * #2108 — whether CS3 has an email address for this person,
                     * so the ticket form can say what is missing BEFORE they
                     * write out their problem (issue item 4). A boolean from
                     * CS3's handoff; the address itself never comes here.
                     *
                     * ⚠ EASY TO FORGET, AND SILENT WHEN FORGOTTEN: `me` is a
                     * hand-copied subset of the session, so a field added to
                     * the session and not to this list reads as undefined —
                     * which `canTicket` turns into "no address on file" and
                     * every ticket form into the no-email notice. That is
                     * exactly what the first render of this panel did.
                     */
                    hasEmail: session.hasEmail === true,
                };

                // The CA's master switch is off for this client: there is
                // nothing to list and nothing to listen to. `visible` hides
                // the panel; this just stops us asking.
                if (!session.chatVisible) {
                    this.liveState = 'ready';

                    return;
                }

                const channels = await fetchChannels(session);
                /*
                 * #2098 — give every direct channel its panel identity before
                 * anything renders: who it is with, and the OTHER person's name
                 * rather than the symmetric "A & B" the server stores.
                 */
                channels.forEach((c) => labelDirect(c, session.uid, session.name, this.knownNames));
                /*
                 * #2108 — and give the support conversation its own identity
                 * before anything renders: the group no heading uses (so it
                 * cannot appear among the client's channels) and the fixed
                 * label the pinned row shows.
                 */
                channels.forEach((c) => labelSupport(c));
                this.channels = channels;
                this.liveState = 'ready';

                /*
                 * #2110 — the counts, BEFORE the listeners attach. The launcher
                 * badge has to be right before anyone opens the panel, and the
                 * first answer is the baseline every later rise is measured
                 * against — so it must land before Firestore replays the
                 * channel documents and the first refresh is asked for. Not
                 * fatal: a failure leaves the badge at zero and logs why.
                 */
                await this.refreshUnread();

                // #2101 — the client's schedule and outside-hours switches.
                await this.loadHours(session);

                // #2118 — the employer's contacts. Employers only, and never
                // on the critical path: see loadContacts().
                await this.loadContacts(session);

                /*
                 * #2098 / cs3-chat #23 — presence. Null when this silo has no
                 * Realtime Database (`attachPresence` says why), in which case
                 * there are simply no dots and nothing else changes.
                 *
                 * Not awaited into the critical path's result: a blocked
                 * websocket must not hold up the channel list.
                 */
                // ⚠ On `this`, not in `data()` — the same reason as `this.chat`:
                // the presence client holds the Firebase Database instance, and
                // Vue 2 would walk it and rewrite the SDK's internals with
                // reactive getters. Only `this.online` is reactive.
                this.presence = await attachPresence(session, {
                    onChange: (uid, isUp) => this.$set(this.online, uid, isUp),
                });
                this.syncPresence();

                if (channels.length && !channels.some((c) => c.id === this.openId)) {
                    /*
                     * The preview's default openId does not exist on a real
                     * server; land on the first channel we can actually read.
                     *
                     * ⚠ #2108 — NOT THE SUPPORT CONVERSATION, unless it is all
                     * there is. "CallSteward Support" sorts near the top of a
                     * roster ordered by name, so without this preference the
                     * panel would open on Support for everybody — which would
                     * both bury the client's own channels and fire an
                     * availability request on every sign-in.
                     */
                    const first = channels.find((c) => !isSupport(c)) || channels[0];
                    this.openId = first.id;
                }

                /*
                 * ⚠ TWO DIFFERENT LISTENERS, FOR TWO DIFFERENT JOBS.
                 *
                 *   every channel's DOC   the list previews, the unread counts
                 *                         and the toast — all of which describe
                 *                         channels nobody is looking at, so this
                 *                         cannot be narrowed to the open one
                 *   the open channel's
                 *   MESSAGES              the conversation on screen
                 *
                 * Attaching a 50-message listener to every channel instead
                 * costs roughly fifty times as much on every mount (spec §12
                 * prices a channel doc at 1 read and opening a channel at ~50),
                 * and Firestore has no hard spend cap.
                 */
                await this.watchChannelDocs(channels.map((c) => c.id));
                if (this.openId) this.watchOpenChannel();
            } catch (e) {
                this.liveState = 'failed';
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                // Logged as well as shown: the code alone does not say which
                // request failed, and this is the only place that knows.
                console.warn('[chat] sign-in failed', e);
            }
        },

        /**
         * #2101 — load the client's chat settings, for the hours strip and the
         * composer rule.
         *
         * ⚠ A FAILURE HERE MUST NOT BREAK CHAT, AND MUST NOT CLOSE THE
         * COMPOSER. `liveSettings` stays null, the strip is not drawn, and
         * `mayPost` falls back to letting the server decide with its 403 —
         * which is exactly the #2096 behaviour this issue improves on. The
         * alternative (treat "unknown" as closed) would make an unrelated
         * settings outage take posting away from every member of every client.
         *
         * ⚠ AND CS STAFF HAVE NO CLIENT. `fetchClientSettings` refuses without
         * a cid (`chat_no_client`), which is correct — staff are not scoped to
         * a client and are never restricted by one's schedule — so it is not
         * even called, rather than called and caught.
         */
        async loadHours(session) {
            if (!session || !session.cid) return;

            try {
                this.liveSettings = await fetchClientSettings(session);
            } catch (e) {
                this.liveSettings = null;
                // Not surfaced as `liveError`: that line is about chat being
                // unusable, and chat is perfectly usable without the strip.
                console.warn('[chat] could not read office hours', e);
            }
        },

        // ── #2108: CallSteward Support ───────────────────────────────────────

        /**
         * Ask whether support is live, and render whichever state comes back.
         *
         * ⚠ IT NEVER THROWS AND NEVER LEAVES THE PANEL WITHOUT A STATE.
         * `fetchSupportAvailability` answers null when it could not ask at all,
         * and `availabilityFailed`'s shape is used for that — live, with a line
         * saying it could not be checked. That is the opposite of the server's
         * own fail-closed rule, deliberately: see `chatSupport.availabilityFailed`
         * for why the two questions take opposite defaults.
         */
        async checkSupport() {
            this.supportChecking = true;

            try {
                const answer = await fetchSupportAvailability(this.chat);
                // A second open while the first request was out: the newest
                // answer is the one to keep, and this is the only writer.
                this.support = answer || availabilityFailed();
            } finally {
                this.supportChecking = false;
            }
        },

        /**
         * Submit the ticket.
         *
         * ⚠ THIS CREATES A REAL CONVERSATION IN THE LIVE HELP DESK on a
         * configured deployment, so it happens only on this click — nothing
         * retries it, and a failure leaves the DRAFT IN PLACE so the person can
         * press it again rather than retype their problem into a box that
         * emptied itself.
         */
        async sendTicket() {
            if (!this.ticketReady || this.ticketSending) return;

            this.ticketSending = true;
            this.ticketError = null;

            try {
                const { ticket } = await submitSupportTicket(this.chat, this.ticketDraft);
                this.supportTicket = ticket;
                this.ticketDraft = '';
            } catch (e) {
                this.ticketError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                console.warn('[chat] support ticket failed', e);
            } finally {
                this.ticketSending = false;
            }
        },

        /** Back to an empty ticket form, after one has been opened. */
        newTicket() {
            this.supportTicket = null;
            this.ticketDraft = '';
            this.ticketError = null;
            // The desk may have opened while they were writing the last one.
            this.checkSupport();
        },

        // ── #2098: the person search and the direct conversation ────────────

        /**
         * Typing in the search box.
         *
         * ⚠ DEBOUNCED, because this is a server query with a `LIKE` on it and
         * not a local filter. At 250 ms a name typed at speed is one request
         * instead of eight.
         *
         * ⚠ AND SHORT QUERIES ARE NOT SENT AT ALL. cs3-chat's `DirectReach`
         * returns `[]` below two characters rather than an error, so asking on
         * the first keystroke is a round trip that can only answer nothing —
         * and one character against a local of two thousand is an enumeration,
         * not a search, which is why the floor exists on both sides.
         */
        onSearchInput() {
            clearTimeout(this.searchTimer);
            this.peopleError = null;
            this.directError = null;

            if (!searchable(this.peopleQuery)) {
                this.people = [];
                this.peopleState = 'idle';

                return;
            }

            this.peopleState = 'typing';
            this.searchTimer = setTimeout(() => this.runSearch(), DEBOUNCE_MS);
        },

        async runSearch() {
            const query = this.peopleQuery;
            if (!this.chat || !searchable(query)) return;

            this.peopleState = 'searching';

            try {
                const people = await searchPeople(this.chat, query);

                /*
                 * The box has moved on while this was in flight — a slow
                 * network and a fast typist. Publishing now would show results
                 * for a prefix that is no longer on screen, which reads as the
                 * search returning the wrong people.
                 */
                if (this.peopleQuery !== query) return;

                this.people = people;
                this.peopleState = 'done';
                // Remember the names, so a conversation with one of them can be
                // labelled even when the symmetric stored name cannot be split
                // (see chatPeople.directLabel).
                people.forEach((p) => this.$set(this.knownNames, p.uid, p.name));
                this.syncPresence();
            } catch (e) {
                if (this.peopleQuery !== query) return;
                this.people = [];
                this.peopleState = 'failed';
                this.peopleError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                console.warn('[chat] person search failed', e);
            }
        },

        /**
         * Open the conversation with a person picked from the search.
         *
         * ⚠ THE SERVER DECIDES, AND IT RE-CHECKS. A 403 here is the normal
         * answer to a stale result — someone who has left the client, or whose
         * shared group was switched off since the list was drawn — so it is
         * reported in those words rather than as "chat is unavailable".
         */
        /**
         * #2118 — load "Your contacts" for an employer.
         *
         * ⚠ EMPLOYERS ONLY, AND THE ROLE IS CHECKED BEFORE THE REQUEST. Every
         * other role is answered `[]` by the chat service, so asking would be a
         * round trip per panel open across the whole fleet to be told nothing.
         *
         * ⚠ NEVER FATAL. A failure here leaves the section showing its own
         * sentence and the rest of the panel exactly as it was — the channel
         * list, Support and search do not depend on it, and chat was usable
         * without this section until today. The same contract `loadHours` has.
         */
        async loadContacts(session) {
            if (!this.showsContacts) return;

            this.contactsState = 'loading';
            this.contactsError = null;

            try {
                this.contactGroups = await fetchContacts(session);
                this.contactsState = 'ready';
            } catch (e) {
                this.contactGroups = [];
                this.contactsError = e && e.code ? e.code : 'chat_unreachable';
                this.contactsState = 'failed';
            }
        },

        async pickPerson(person) {
            if (!this.chat || this.opening) return;

            this.opening = person.uid;
            this.directError = null;

            try {
                const channel = labelDirect(
                    await openDirect(this.chat, person.uid),
                    this.me ? this.me.uid : '',
                    this.me ? this.me.name : '',
                    this.knownNames,
                );

                /*
                 * Already on the roster when the conversation existed before —
                 * replace nothing, because the row carries live state (unread,
                 * the last message) that this response does not have.
                 */
                const existing = this.channels.find((c) => c.id === channel.id);
                if (!existing) {
                    this.channels.push(channel);
                    /*
                     * ⚠ THE DOC LISTENER HAS TO BE RE-ATTACHED. It is one
                     * subscription over the roster's ids, attached at sign-in —
                     * so a conversation opened afterwards would have no unread
                     * count, no list preview, no toast and no chime until the
                     * next page load.
                     */
                    await this.watchChannelDocs(this.channels.map((c) => c.id));
                }

                this.closeSearch();
                this.openChannel(channel.id);
                this.syncPresence();
            } catch (e) {
                this.directError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                console.warn('[chat] could not open a conversation', e);
            } finally {
                this.opening = null;
            }
        },

        closeSearch() {
            clearTimeout(this.searchTimer);
            this.searchOpen = false;
            this.peopleQuery = '';
            this.people = [];
            this.peopleState = 'idle';
            this.peopleError = null;
            // The watch list shrinks back to the conversations on the roster.
            this.syncPresence();
        },

        /** Point the presence client at exactly the people on screen. */
        syncPresence() {
            if (this.presence) this.presence.watch(this.presenceUids);
        },

        /** #2098 — is this person online? Used by the rows and the header. */
        isOnline(uid) { return this.online[uid] === true; },

        /**
         * The avatar for a direct channel's row.
         *
         * Keyed on the OTHER person's uid where we know it, so one person is
         * the same colour and the same initials in the search result, the
         * conversation row and the message header. Falls back to the channel
         * when the key could not be read — a conversation still has to render.
         */
        colourOf(channel) {
            return colourFor((channel && channel.otherUid) || (channel && channel.id) || '');
        },
        initialsOf(channel) {
            return initialsFor((channel && channel.label) || '?');
        },

        /** #2098 — the "CallSteward" tag beside a support person in the results. */
        isStaffPerson(role) { return isStaffRole(role); },

        // ── #2102: attaching a file ─────────────────────────────────────────

        sizeLabel,
        kindLabel,

        /** The paperclip opens the hidden file input. */
        pickFile() {
            this.attachError = null;
            if (this.$refs.fileInput) this.$refs.fileInput.click();
        },

        onFilePicked(event) {
            const input = event.target;
            this.addFiles(input.files);
            /*
             * ⚠ CLEARED, OR THE SAME FILE CANNOT BE PICKED TWICE. A file input
             * fires `change` only when the value CHANGES, so re-picking the
             * photo you just removed would do nothing at all — which reads as
             * the paperclip being broken.
             */
            input.value = '';
        },

        /**
         * Ctrl+V in the composer.
         *
         * ⚠ THE EVENT IS ONLY INTERCEPTED WHEN THERE IS AN IMAGE. Pasting text
         * into chat has to keep working exactly as it does (issue item 6), so
         * `preventDefault` is called only once an image is actually found —
         * otherwise a `preventDefault` on every paste would stop people pasting
         * a gate code into the box, which would be a worse bug than the one
         * being fixed.
         *
         * ⚠ AND IT RESPECTS `mayAttach`, like the paperclip and the drop zone.
         * When posting is closed there is no paperclip, so a paste that
         * uploaded anyway would be the one way round a read-only composer — and
         * the server would refuse it, after the bytes had crossed the network.
         */
        onPaste(event) {
            if (!this.mayAttach) return;

            const images = imagesFromPaste(event.clipboardData);
            if (!images.length) return;      // a text paste: leave it alone

            event.preventDefault();
            this.addFiles(images);
        },

        onDrop(event) {
            this.dragging = false;
            if (!this.mayAttach) return;
            const dt = event.dataTransfer;
            if (dt && dt.files) this.addFiles(dt.files);
        },

        /**
         * Queue files and start uploading them.
         *
         * ⚠ THE CLIENT-SIDE CHECK IS A COURTESY, NOT THE GATE. cs3-chat sniffs
         * the CONTENT of every upload; a browser's `File.type` is the operating
         * system guessing from the extension, and it is empty for HEIC on much
         * of Android. So `refusalFor` only stops what it positively recognises
         * as wrong — a 40 MB file, a video, a zip — which saves a doomed upload
         * over a venue's wifi, and anything it is unsure about is sent for the
         * server to decide on the bytes.
         */
        addFiles(fileList) {
            const files = [...(fileList || [])];
            if (!files.length) return;

            this.attachError = null;

            files.forEach((file) => {
                if (this.pending.length >= MAX_PER_MESSAGE) {
                    this.attachError = 'A message can carry ' + MAX_PER_MESSAGE + ' files.';

                    return;
                }

                const refusal = refusalFor(file);
                if (refusal) {
                    // Named, because "a file was refused" with three files
                    // queued says nothing about which.
                    this.attachError = file.name + ': ' + refusalText(refusal);

                    return;
                }

                const row = {
                    key: ++this.pendingKey,
                    name: file.name,
                    size: file.size,
                    type: file.type || '',
                    pct: 0,
                    state: 'uploading',
                    error: null,
                    id: null,
                };
                this.pending.push(row);
                this.startUpload(row, file);
            });
        },

        /**
         * Upload one queued file.
         *
         * ⚠ THE ABORT CONTROLLER IS KEPT OFF `data()`, on `this.aborts`. It is a
         * live browser object with its own internals, and Vue 2 walks everything
         * in `data()` recursively and rewrites it with reactive getters — the
         * same reason the chat session and the presence client live on `this`.
         */
        async startUpload(row, file) {
            if (!this.chat) return;

            const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
            if (!this.aborts) this.aborts = {};
            this.aborts[row.key] = controller;

            try {
                const saved = await uploadAttachment(
                    this.chat, this.openId, file,
                    (pct) => { row.pct = pct; },
                    controller ? controller.signal : undefined,
                );

                if (!saved || !saved.id) throw new ChatUnavailable('attachment_store_failed');

                row.id = saved.id;
                // The server's sanitised name, not the client's: it is what
                // every other reader of this message will see.
                row.name = saved.name || row.name;
                row.type = saved.type || row.type;
                row.size = saved.size || row.size;
                row.pct = 100;
                row.state = 'ready';
            } catch (e) {
                const code = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';

                // A cancel is not a failure: the row is already gone.
                if (code === 'upload_cancelled') return;

                row.state = 'failed';
                row.error = refusalText(code);
                console.warn('[chat] attachment upload failed', e);
            } finally {
                delete this.aborts[row.key];
            }
        },

        /**
         * Remove a queued file, aborting the upload if it is still running.
         *
         * ⚠ THE REQUEST IS ABORTED, NOT JUST THE ROW REMOVED. Without the
         * abort, cancelling a 10 MB upload stops showing it and keeps sending
         * it — the bytes still cross the network, the server still stores the
         * file, and the prune collects it a day later as an orphan.
         */
        removeFile(key) {
            const controller = this.aborts && this.aborts[key];
            if (controller) {
                try { controller.abort(); } catch (e) { /* already finished */ }
                delete this.aborts[key];
            }
            this.pending = this.pending.filter((p) => p.key !== key);
            this.attachError = null;
        },

        /** Retry one that failed, by picking it again — the file is gone from here. */
        clearFailed() {
            this.pending = this.pending.filter((p) => p.state !== 'failed');
            this.attachError = null;
        },

        // ── #2102: showing what was sent ────────────────────────────────────

        /**
         * The object URL for a photo, fetching it the first time it is seen.
         *
         * ⚠ THE PRE-SIGNED URL CANNOT GO IN AN `<img src>` AND BE CACHED. It
         * expires in five minutes, and cs3-chat serves the object
         * `Content-Disposition: attachment`, so a browser would download it
         * rather than render it. The bytes are fetched once and kept as an
         * object URL, which never expires — see `chatAttachments.createFileCache`.
         *
         * ⚠ AND THE FETCH IS CROSS-ORIGIN TO S3, so it needs a CORS rule on the
         * bucket. When it fails — no CORS, a network drop, an expired link —
         * `fetchAttachment` returns null and the thumbnail falls back to the
         * file chip, whose download link is a top-level navigation and works
         * without CORS. Raised for host-pm in the READY.
         */
        async thumbFor(attachment) {
            if (!attachment || !attachment.thumbnail || !this.chat) return null;

            const held = this.files ? this.files.get(attachment.id) : null;
            if (held) return held;
            if (this.fetching[attachment.id]) return null;

            this.$set(this.fetching, attachment.id, true);
            try {
                const got = await fetchAttachment(this.chat, attachment.id);
                if (got && got.blob && this.files) {
                    const url = this.files.put(attachment.id, got.blob);
                    // Reactive, so the <img> that asked for it re-renders.
                    this.$set(this.thumbs, attachment.id, url);

                    return url;
                }
                // Null: the chip is shown instead, which still downloads.
                this.$set(this.thumbs, attachment.id, null);
            } catch (e) {
                this.$set(this.thumbs, attachment.id, null);
                console.warn('[chat] could not load a thumbnail', e);
            } finally {
                this.$delete(this.fetching, attachment.id);
            }

            return null;
        },

        /**
         * Open a PDF or download a file.
         *
         * ⚠ FETCHED AT THE MOMENT OF THE CLICK, never stored. Five minutes is
         * enough to open a tab and far too short to keep — and a stored link is
         * a bearer token sitting in memory for an object the holder may stop
         * being allowed to read.
         *
         * ⚠ AND `window.open` HAS TO BE CALLED FROM THE CLICK, not after the
         * await: a pop-up opened from a promise callback is blocked by every
         * browser, because it is no longer in a user gesture. So the tab is
         * opened first and navigated once the URL arrives.
         */
        async openAttachment(attachment) {
            if (!this.chat) return;

            let tab = null;
            try { tab = window.open('', '_blank'); } catch (e) { tab = null; }

            try {
                const url = await attachmentUrl(this.chat, attachment.id);
                if (!url) throw new ChatUnavailable('attachment_store_failed');

                if (tab && !tab.closed) {
                    tab.location = url;
                } else {
                    // Pop-ups blocked: navigate this tab instead. The file is
                    // served Content-Disposition: attachment, so this downloads
                    // rather than replacing the page.
                    window.location.assign(url);
                }
            } catch (e) {
                if (tab && !tab.closed) tab.close();
                const code = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                this.attachError = refusalText(code);
                console.warn('[chat] could not open an attachment', e);
            }
        },

        /**
         * Fetch the bytes for any photo on screen we do not hold yet.
         *
         * ⚠ ONLY WHAT IS ON SCREEN, and only once each. Fetching every
         * attachment in a 120-message scrollback would be 120 signed links and
         * 120 S3 GETs on opening a channel; the `fetching` guard stops two
         * renders of the same thumbnail both asking.
         */
        ensureThumbs() {
            if (!this.isLive || !this.chat || this.view !== 'channel') return;

            this.slice.forEach((m) => {
                (m.attachments || []).forEach((a) => {
                    if (!a.thumbnail) return;
                    if (this.thumbs[a.id] || this.fetching[a.id]) return;
                    this.thumbFor(a);
                });
            });
        },

        /** Open a photo larger. The bytes are already held. */
        showLarger(attachment) {
            const url = this.thumbs[attachment.id];
            if (url) this.lightbox = { url, name: attachment.name };
        },

        // ── #2101: the chime, the flash, and their settings ─────────────────

        /**
         * The tab became the one being looked at: stop calling attention to it.
         *
         * `document.hidden` is checked rather than trusted blindly, because a
         * `focus` event can fire on a window that is still behind another.
         */
        onTabAttention() {
            if (typeof document !== 'undefined' && document.hidden) return;
            this.stopFlash();
        },

        /** Persist a preference change. A storage failure is not an error. */
        saveNotify() {
            writePrefs(this.notify);
            // Turning the flash off while it is running stops it now rather
            // than at the next message, which is what "off" means.
            if (this.notify.flash === false) this.stopFlash();
        },

        /**
         * The volume slider's input. `<input type=range>` gives a STRING, and
         * `0.5` arriving as `"0.5"` would be stored as a string and compared
         * against numbers elsewhere.
         */
        setVolume(value) {
            const n = Number(value);
            this.notify.volume = isFinite(n) ? Math.min(1, Math.max(0, n)) : 0.5;
            this.saveNotify();
        },

        /**
         * Play the chime at the user's volume.
         *
         * ⚠ CREATED LAZILY AND NEVER IN `data()`. An AudioContext in Vue 2's
         * reactive data would have Vue rewrite the SDK's own internals with
         * reactive getters — the same reason the chat session lives on `this`
         * rather than in `data()`.
         */
        playChime() {
            if (!this.chime) this.chime = createChime();
            // Audio blocked before a user gesture is item 6's "fail quietly":
            // play() returns false and nothing is shown. The flash still works.
            this.chime.play(this.notify.volume);
        },

        /** Start the tab title (and the launcher) blinking. */
        startFlash() {
            if (!this.titleFlasher) this.titleFlasher = createTitleFlasher();
            // Only the DOCKED page owns the tab title. In the pop-out the
            // window is the chat, so there is nothing to call attention to.
            if (!this.windowMode && this.titleFlasher.start('💬 New message')) {
                this.flashing = true;
            }
        },

        /** Stop blinking and put the real title back. */
        stopFlash() {
            if (this.titleFlasher) this.titleFlasher.stop();
            this.flashing = false;
        },

        /**
         * Listen to every channel's own Firestore document.
         *
         * One subscription covering all of them, replaced wholesale when the
         * roster changes — the roster only changes on a fresh sign-in, so this
         * is attached once per session.
         */
        async watchChannelDocs(channelIds) {
            if (!this.chat || !this.chat.firestore || !channelIds.length) return;

            this.detachDocs();

            try {
                this.docListener = await listenToChannelDocs(
                    this.chat, channelIds,
                    (id, docData) => this.onChannelDoc(id, docData),
                    (id, err) => {
                        this.liveError = err.code;
                        console.warn('[chat] channel-doc listener failed on ' + id, err);
                    }
                );
            } catch (e) {
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_listen_failed';
                console.warn('[chat] could not watch channel docs', e);
            }
        },

        /**
         * Listen to the OPEN channel's messages, at the current scrollback.
         *
         * Idempotent and single: the previous message listener is detached
         * first, whichever channel it belonged to. Called on open and again on
         * every `loadOlder`, because widening the scrollback in Firestore means
         * a wider query rather than a slice of something already held.
         */
        async watchOpenChannel() {
            if (!this.chat || !this.chat.firestore || !this.openId) return;

            const channelId = this.openId;
            this.detachOpen();

            try {
                const stop = await listenToChannel(
                    this.chat, channelId, this.shown,
                    (messages) => this.onLiveMessages(channelId, messages),
                    (err) => {
                        this.liveError = err.code;
                        console.warn('[chat] listener failed on ' + channelId, err);
                    }
                );

                /*
                 * The user can open another channel while this await is in
                 * flight. Publishing then would leave the newer listener
                 * orphaned and this stale one feeding the panel, so the late
                 * arrival is dropped instead.
                 */
                if (this.openId !== channelId) {
                    stop();

                    return;
                }

                this.openListener = stop;
                this.openListenerFor = channelId;
            } catch (e) {
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_listen_failed';
                console.warn('[chat] could not listen to ' + channelId, e);
            }
        },

        detachOpen() {
            if (!this.openListener) return;
            try { this.openListener(); } catch (e) { /* already gone */ }
            this.openListener = null;
            this.openListenerFor = null;
        },

        detachDocs() {
            if (!this.docListener) return;
            try { this.docListener(); } catch (e) { /* already gone */ }
            this.docListener = null;
        },

        /**
         * A channel's document changed.
         *
         * This is the only listener attached to channels the user is not
         * looking at, so it is what keeps the list previews moving — and, since
         * #2110, it is the TRIGGER for the unread refresh rather than the thing
         * that counts. A new last message means "ask the server again"; the
         * chime, the flash and the toast then follow the server's answer
         * (`refreshUnread`), which already leaves out this person's own messages
         * and whatever they read in another tab.
         *
         * Firestore replays every channel document the moment the listeners
         * attach; those all become one request (`createRefresher` coalesces
         * them), and that request finds nothing risen against the baseline
         * `startLive` loaded — so a page load chimes for nothing, as before.
         */
        onChannelDoc(channelId, docData) {
            const channel = this.channels.find((c) => c.id === channelId);
            if (!channel) return;

            if (applyChannelDoc(channel, docData) && this.refresher) this.refresher.request();
        },

        /**
         * Firestore sent the open channel's messages.
         *
         * Messages only. The channel-list preview and the toast are driven by
         * `onChannelDoc`, because they describe channels this listener is not
         * attached to. What this one knows that nothing else does is the NEWEST
         * MESSAGE ON SCREEN — which is the read mark (#2110).
         */
        onLiveMessages(channelId, messages) {
            this.$set(this.liveMessages, channelId, messages);
            this.syncReading();
        },

        /**
         * #2110 — ask the server for the counts, and act on what rose.
         *
         * Called once from `startLive` (the baseline) and afterwards only
         * through `this.refresher`, which turns a burst of triggers — Firestore
         * replaying the channel documents, several messages in one minute, a
         * mark landing while a fetch was out — into one request at a time.
         *
         * The rises are where the #2101 notifications now come from. The chime
         * and the flash still ask `chatNotify` (your own messages never rise,
         * so `mine` is simply false; `primed` is the baseline flag; `looking`
         * is the real question), and the toast is raised for the last channel
         * that rose while not on screen. A count that FELL — read on the phone,
         * or in another tab — just updates the row.
         */
        async refreshUnread() {
            if (!this.chat) return;

            let unread;
            try {
                unread = await fetchUnread(this.chat);
            } catch (e) {
                console.warn('[chat] could not read the unread counts', e);

                return;
            }
            // The session can be gone by the time the answer lands.
            if (!this.chat) return;

            const { total, rose } = applyUnreadCounts(this.channels, unread, { baseline: this.unreadLoaded });
            this.unreadServerTotal = total;
            this.unreadLoaded = true;

            let chime = false;
            let flash = false;
            let toastFor = null;
            rose.forEach((id) => {
                const event = { mine: false, looking: this.lookingAt(id), primed: true };
                if (shouldChime(this.notify, event)) chime = true;
                if (shouldFlash(this.notify, event)) flash = true;
                if (!event.looking) toastFor = this.channels.find((c) => c.id === id) || toastFor;
            });
            // One chime for a burst, however many channels rose at once.
            if (chime) this.playChime();
            if (flash) this.startFlash();
            if (toastFor) this.showLiveToast(toastFor);
        },

        /** #2110 — `isLooking` over this component's state. */
        lookingAt(channelId) {
            return isLooking({
                collapsed: this.collapsed,
                view: this.view,
                openId: this.openId,
                windowMode: this.windowMode,
                undocked: this.undocked,
                hidden: typeof document !== 'undefined' && document.hidden === true,
            }, channelId);
        },

        /**
         * #2110 — tell the marker what is on screen. Idempotent, and called from
         * every place the answer can change: a channel opened or left, the
         * panel expanded or collapsed, the tab shown or hidden, messages
         * arriving. The marker itself decides whether that is worth a request
         * (`createReadMarker`): only a new channel or a newer message is.
         */
        syncReading() {
            if (!this.isLive || !this.marker) return;

            if (this.lookingAt(this.openId)) {
                this.marker.look(this.openId, newestShownId(this.liveMessages[this.openId]));
            } else {
                this.marker.leave();
            }
        },

        /**
         * #2110 — the marker's request: `PUT /api/channels/{id}/read`.
         *
         * The answer carries that channel's count, which zeroes the row and
         * moves the badge at once; a refresh is then asked for so the total is
         * the server's own figure rather than our arithmetic. A failure is
         * logged and nothing else: the next real change marks again, and the
         * count on screen stays what the server last said.
         */
        async sendMark(channelId, messageId, viewing) {
            if (!this.chat) return;

            try {
                const result = await markRead(this.chat, channelId, messageId, viewing);
                if (!this.chat) return;
                this.unreadServerTotal = applyMarkResult(this.channels, result, this.unreadServerTotal);
                if (this.refresher) this.refresher.request();
            } catch (e) {
                console.warn('[chat] could not mark ' + channelId + ' read', e);
            }
        },

        /**
         * #2110 — the tab was shown or hidden, in either mode.
         *
         * Hidden: the open conversation is no longer being read, so the marker
         * says `viewing: false`. Shown: it is again — and whatever another tab
         * or the phone read meanwhile is fetched, which is what makes "a second
         * tab shows the same numbers" true at the moment someone looks.
         */
        onReadingVisibility() {
            this.syncReading();
            if (typeof document !== 'undefined' && document.hidden) return;
            if (this.refresher) this.refresher.request();
        },

        /**
         * The 60-second toast (spec §5), for a real message.
         *
         * Built from the channel DOCUMENT's `lastSender` / `lastSnippet`, which
         * is what spec §7 says those fields are for — the toast has to work for
         * a channel with no message listener attached, so the message itself is
         * not available here and must not be fetched for it.
         *
         * Separate from the preview's `showToast`, which invents one on demand
         * from a fixed channel: that is a demo control, this is an event. They
         * share the timer so the two can never both be up.
         */
        showLiveToast(channel) {
            // #2110 — read off the row, where `applyChannelDoc` keeps the
            // document's `lastSender` / `lastSnippet`: the toast is raised when
            // the server's count rises, not when the document arrives.
            const who = channel.lastSender || 'CallSteward';

            this.toast = {
                // #2098 — a person for a conversation, `#channel` otherwise.
                // `channel.name` on a direct channel is the symmetric "A & B".
                where: channel.grp === 'direct' ? channel.label : '#' + channel.name,
                whereId: channel.id,
                who: { n: who, i: initialsFor(who), c: colourFor(channel.lastSenderUid || who) },
                body: channel.lastSnippet || '',
            };
            this.toastLeft = 60;
            clearInterval(this.toastTimer);
            this.toastTimer = setInterval(() => {
                this.toastLeft -= 1;
                if (this.toastLeft <= 0) this.hideToast();
            }, 1000);
        },

        /**
         * Post a message for real.
         *
         * ⚠ NO OPTIMISTIC ECHO. The sent message appears when Firestore sends
         * it back, which is the same path everyone else's messages take. An
         * optimistic line would have to be reconciled with the real one on
         * arrival (same message, different id, possibly sanitised differently —
         * spec §5 strips formatting and rewrites look-alike hostnames), and a
         * message shown as sent that the server then refused is worse than a
         * quarter-second of nothing.
         *
         * The draft is only cleared once the server has it, for the same
         * reason: a refused message the user has to retype is a lost message.
         */
        async sendLive(text) {
            if (this.sending || !this.chat || !this.channel) return;

            this.sending = true;
            this.liveError = null;

            /*
             * #2102 — only the uploads that FINISHED go with the message. One
             * still at 60% has no server id to send, and waiting for it here
             * would freeze the composer on a slow upload; the Send button is
             * disabled while any upload is running, so this is the belt to that
             * braces.
             */
            const ids = this.readyAttachments.map((p) => p.id);

            try {
                await sendMessage(this.chat, this.channel.id, text, ids);
                this.draft = '';
                /*
                 * ⚠ CLEARED ONLY ON SUCCESS. A refusal — outside office hours,
                 * a job window that closed while the file uploaded — leaves the
                 * draft AND the queued files exactly where they were, so one
                 * 403 does not make someone pick their photos again. The server
                 * keeps them as unbound uploads and collects them within the
                 * day if they are never sent.
                 */
                this.pending = [];
                this.attachError = null;
                this.$nextTick(() => this.scrollToNewest());
            } catch (e) {
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                console.warn('[chat] send failed', e);
            } finally {
                this.sending = false;
            }
        },
        openSettings() {
            this.collapsed = true;
            if (this.$route.path !== '/configuration') this.$router.push({ path: '/configuration' });
        },
        showToast() {
            const c = this.channels.find((x) => x.id === 'c7_job_18420');
            if (!c || c.see.indexOf(this.role) < 0) return;
            this.toast = {
                where: '#' + c.name,
                whereId: c.id,
                who: { n: 'Dana Whitfield', i: 'DW', c: '#7a5bb5' },
                body: 'Gate code changed for tomorrow’s load-in — it’s in the job notes now.',
            };
            this.toastLeft = 60;
            clearInterval(this.toastTimer);
            this.toastTimer = setInterval(() => {
                this.toastLeft -= 1;
                if (this.toastLeft <= 0) this.hideToast();
            }, 1000);
        },
        hideToast() {
            clearInterval(this.toastTimer);
            this.toastTimer = null;
            this.toast = null;
        },
        openFromToast() {
            const id = this.toast && this.toast.whereId;
            this.hideToast();
            this.collapsed = false;
            if (id) this.openChannel(id);
        },
    },
    created() {
        /*
         * #2096 — the live chat session, declared OUTSIDE `data()` on purpose.
         *
         * It holds the Firebase app and the Firestore instance, and Vue 2
         * recursively makes everything in `data()` reactive — which would mean
         * rewriting the SDK's internals with reactive getters. Declared here so
         * the field exists from the start rather than appearing mid-flight, and
         * so the reason is recorded next to it.
         *
         * Nothing in a template or computed may read this. The plain values the
         * UI needs are mirrored into `me`, which IS reactive.
         */
        this.chat = null;

        /*
         * #2102 — the fetched-attachment cache, and the in-flight uploads'
         * abort controllers. Both are OUTSIDE `data()` for the same reason as
         * `this.chat`: the cache owns Blobs and object URLs, an AbortController
         * is a live browser object, and Vue 2 would walk and rewrite both.
         *
         * ⚠ THE CACHE MUST BE RELEASED. `URL.createObjectURL` pins a blob in
         * memory until it is revoked or the document goes, so a panel that
         * loaded fifty photos over a shift would hold fifty blobs for the life
         * of the tab. `beforeDestroy` calls `release()`.
         */
        this.files = createFileCache({});
        this.aborts = {};

        /*
         * #2110 — the read marker and the count refresher. Off `data()` like
         * the session: they own timers, and nothing in a template reads them.
         * Created here rather than in `startLive` so `syncReading` can be
         * called from a watcher before sign-in finishes and simply find the
         * session missing (`sendMark` checks).
         */
        this.marker = createReadMarker({ send: (id, messageId, viewing) => this.sendMark(id, messageId, viewing) });
        this.refresher = createRefresher({ run: () => this.refreshUnread() });
    },
    watch: {
        /*
         * #2110 — everything the read mark depends on. `syncReading` is
         * idempotent, so watching each input separately costs nothing and
         * misses nothing: opening a channel (`openId`, `view`), going back to
         * the list (`view`), collapsing or expanding (`collapsed`), undocking
         * (`undocked`). Expanding also re-asks for the counts, so a panel opened
         * after an hour shows what the phone shows.
         */
        openId() { this.syncReading(); },
        view() { this.syncReading(); },
        collapsed(now) {
            this.syncReading();
            if (!now && this.refresher) this.refresher.request();
        },
        undocked() { this.syncReading(); },
        /**
         * #2091 r2 — tell App.vue when the panel is open so the layout can make
         * room: the left rail slides away and the middle gets both its 60px and
         * clearance from this panel.
         *
         * `visible` is in the condition, not just `collapsed`: chat being
         * switched off in Configuration hides the panel without touching
         * `collapsed`, and without this the page would keep a 368px gutter
         * reserved for a panel that is no longer there.
         *
         * `immediate` because the panel can mount already open — a reload after
         * the user opened it — and the layout has to agree from the first paint.
         */
        panelIsOpen: { handler(open) { this.$emit('panel-open', open); }, immediate: true },

        /*
         * #2102 — load the thumbnails for whatever is on screen.
         *
         * ⚠ KEYED ON THE IDS, NOT ON THE MESSAGE ARRAY. `slice` is a computed
         * that returns a new array on every render, so watching it directly
         * would fire on every unrelated change and re-walk the conversation.
         * The joined list of attachment ids changes only when the visible
         * attachments do, which is exactly when there is work to do.
         */
        visibleAttachmentIds: {
            handler() { this.ensureThumbs(); },
            immediate: true,
        },
    },
    mounted() {
        this.clock = setInterval(() => { this.now = new Date(); }, 60000);

        /*
         * #2096 — sign in to chat as soon as the panel exists, collapsed or
         * not. The channel list, the unread counts and the toast all have to be
         * right BEFORE the user opens the panel — that is what the launcher's
         * unread badge is — so waiting for the first open would show a stale
         * zero and no toast until then.
         *
         * Not awaited: nothing on this page may wait for a chat server.
         */
        // The pop-out is a separate browser window with its own module state,
        // so it signs in for itself rather than inheriting anything.
        if (this.isLive) this.startLive();

        // #2091 r5 — seat the Preview-as picker on the signed-in role, so it
        // opens showing what this user really sees and changes nothing until it
        // is touched. Done here rather than in data() because the role comes
        // from a vuex getter.
        if (!this.viewAs) this.viewAs = 'r:' + this.realRole;

        /*
         * #2110 — BOTH modes, so it is registered before the pop-out returns
         * below: a pop-out window can be hidden behind another just as a tab
         * can, and it is the one most likely to be left open on a conversation.
         */
        if (this.isLive) {
            window.addEventListener('focus', this.onReadingVisibility);
            document.addEventListener('visibilitychange', this.onReadingVisibility);
        }

        if (this.windowMode) return;   // the pop-out neither owns nor watches one

        try {
            this.undocked = window.sessionStorage.getItem(DOCK_KEY) === 'undocked';
        } catch (e) { /* private mode: start docked */ }

        // A reload of the main page loses the window handle but not the state.
        // Re-acquire it by name: window.open with an existing name and no URL
        // returns the SAME window without navigating it, so the pop-out is not
        // reloaded out from under the user. If it is gone, we re-dock.
        if (this.undocked) {
            let existing = null;
            try { existing = window.open('', POPOUT_NAME); } catch (e) { existing = null; }
            if (existing && !existing.closed && existing !== window) {
                this.popout = existing;
                this.watchPopout();
            } else {
                this.setUndocked(false);
            }
        }

        window.addEventListener('message', this.onDockMessage);

        /*
         * #2101 — "blink until the chat is opened or focused".
         *
         * ⚠ BOTH EVENTS, NOT ONE. `focus` alone misses the person who switches
         * back to an already-focused window from another app (no focus event
         * fires in some browsers), and `visibilitychange` alone misses the
         * person who clicks from another window of the same browser onto a tab
         * that was already visible. Together they cover the ways a tab becomes
         * the one being looked at.
         *
         * Registered on the docked panel only: the pop-out does not own the tab
         * title (see `startFlash`), so it has nothing to stop.
         */
        window.addEventListener('focus', this.onTabAttention);
        document.addEventListener('visibilitychange', this.onTabAttention);
    },
    beforeDestroy() {
        clearInterval(this.toastTimer);
        clearInterval(this.clock);
        this.stopWatchingPopout();
        window.removeEventListener('message', this.onDockMessage);
        /*
         * #2101 — stop the title blinking and put the page's real title back
         * before this component goes. A flasher left running after the panel is
         * destroyed owns an interval nothing can cancel and a title nothing
         * will restore, so every CS3 navigation with an unread message would
         * leave the tab permanently reading "💬 New message".
         */
        window.removeEventListener('focus', this.onTabAttention);
        document.removeEventListener('visibilitychange', this.onTabAttention);
        this.stopFlash();
        if (this.chime) { this.chime.close(); this.chime = null; }
        /*
         * #2096 — detach every Firestore listener.
         *
         * ⚠ NOT OPTIONAL HOUSEKEEPING. A listener outlives the component that
         * made it, keeps its connection open, and keeps charging reads —
         * Firestore has no hard spend cap (spec §12), which is why a budget
         * alert on each project is required rather than advisable. Navigating
         * between CS3 pages with the panel mounted would otherwise leave one
         * listener per channel per visit, for the life of the tab.
         *
         * The session itself is dropped too: its handoff was single-use and its
         * listeners are gone, so keeping it would only hand a stale object to
         * the next mount.
         */
        this.detachOpen();
        this.detachDocs();
        /*
         * #2098 — and the presence client, which owns one Realtime Database
         * listener per watched person plus one on `.info/connected`. `stop()`
         * also writes our own `offline`: navigating between CS3 pages destroys
         * this component WITHOUT closing the socket, so Firebase has no
         * disconnect to act on and we would read as online until the tab went.
         */
        clearTimeout(this.searchTimer);
        if (this.presence) { this.presence.stop(); this.presence = null; }
        /*
         * #2102 — revoke every object URL, and abort any upload still in
         * flight. An unrevoked object URL pins its blob for the life of the
         * tab; an unaborted upload keeps sending bytes for a message that can
         * no longer be posted, and leaves the server holding a file the prune
         * collects a day later as an orphan.
         */
        if (this.files) { this.files.release(); this.files = null; }
        Object.keys(this.aborts || {}).forEach((key) => {
            try { this.aborts[key].abort(); } catch (e) { /* already finished */ }
        });
        this.aborts = {};
        /*
         * #2110 — `viewing: false` for whatever was open, BEFORE the session
         * goes: `marker.stop()` sends through `sendMark`, which needs
         * `this.chat`. Then no more refreshes — a timer firing after this
         * would find no session and do nothing, but it would still be a timer
         * nothing owns.
         */
        if (this.isLive) {
            window.removeEventListener('focus', this.onReadingVisibility);
            document.removeEventListener('visibilitychange', this.onReadingVisibility);
        }
        if (this.marker) { this.marker.stop(); this.marker = null; }
        if (this.refresher) { this.refresher.stop(); this.refresher = null; }
        if (this.isLive) resetChatSession();
        this.chat = null;
        // Leaving the page with the panel open must not strand the layout in
        // its made-room state.
        this.$emit('panel-open', false);
    },
};
</script>

<style scoped>
.cp__rail{
    position:fixed;right:20px;bottom:20px;z-index:1040;
    display:flex;align-items:center;gap:9px;
    padding:11px 17px 11px 15px;
    background:#1a2744;color:#fff;border:0;border-radius:24px;
    box-shadow:0 5px 18px rgba(26,39,68,.34);font-size:13.5px;font-weight:600;cursor:pointer;
}
.cp__rail:hover{background:#243654}
.cp__railn{
    min-width:19px;padding:1px 6px;border-radius:9px;background:#fb8b03;color:#3d2200;
    font-size:10.5px;font-weight:700;text-align:center;
}

.cp__panel{
    position:fixed;top:0;right:0;bottom:0;z-index:1041;width:368px;max-width:100vw;
    display:flex;flex-direction:column;
    background:#fff;border-left:1px solid #dcdfe6;box-shadow:-6px 0 22px rgba(26,39,68,.16);
}
.cp__head{
    display:flex;align-items:center;gap:8px;padding:10px 8px 10px 14px;min-height:48px;
    background:#1a2744;color:#fff;
}
.cp__title{flex:1;min-width:0}
.cp__t1{display:block;font-size:14px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__t2{display:block;font-size:11px;color:#9fb3d6;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__ico{
    width:30px;height:30px;flex:0 0 30px;border-radius:4px;display:grid;place-items:center;
    background:transparent;border:0;color:#cfdcf0;cursor:pointer;
}
.cp__ico:hover{background:rgba(255,255,255,.12);color:#fff}
.cp__body{flex:1;min-height:0;overflow-y:auto;overflow-x:hidden}

/* ── #2108 — CallSteward Support, pinned ──────────────────────────────────
   A sibling of the scroller rather than a row inside it, so the list can be
   any length and this stays where it is. The top border is what says "this is
   not part of the list above"; the tint is what makes it readable as the one
   fixed thing in the panel. */
.cp__pin{
    flex:0 0 auto;align-items:center;
    padding:10px 14px;background:#f3f6fb;border-top:1px solid #dcdfe6;
    border-left:3px solid #2451a8;
}
.cp__pin:hover{background:#eaf0fa}
.cp__pinic{
    flex:0 0 22px;width:22px;height:22px;border-radius:50%;display:grid;place-items:center;
    background:#2451a8;color:#fff;font-size:11px;
}

/* The three support states that are not an ordinary composer: checking, the
   ticket form, and the ticket that was opened. Same footprint as the
   office-hours banner so the panel does not jump between them. */
.cp__sup{
    padding:11px 13px;background:#f3f6fb;border-top:1px solid #dcdfe6;
    font-size:12.5px;color:#374151;line-height:1.5;
}
.cp__suph{display:flex;align-items:center;gap:8px;font-size:13px;color:#1f2933}
.cp__supb{margin-top:5px;color:#4a5562}
.cp__supnx{color:#6b7280}
.cp__supno{
    margin-top:8px;padding:8px 9px;background:#fff6e0;border:1px solid #fb8b03;border-radius:4px;
    color:#6b4a00;
}
.cp__supta{
    width:100%;min-height:72px;max-height:160px;resize:vertical;margin-top:8px;padding:8px 9px;
    font:inherit;font-size:12.5px;border:1px solid #cbd2d9;border-radius:4px;background:#fff;
}
.cp__supr{display:flex;align-items:center;gap:9px;margin-top:7px}
.cp__supc{flex:1;font-size:11px;color:#8b949e}
.cp__supe{margin-top:7px;color:#b42318}
.cp__supalt{margin-top:7px;font-size:11.5px;color:#8b949e}
/* The note above a LIVE support composer — `not_configured` or an availability
   request that failed. A line, not a banner: the composer below it works. */
.cp__supline{
    display:flex;align-items:flex-start;gap:7px;margin:0 0 7px;
    font-size:11.5px;color:#6b7280;
}
.cp__supcomp{border-top:1px solid #dcdfe6}

.cp__grp{padding:12px 0 2px}
.cp__grph{
    display:flex;align-items:center;gap:7px;padding:0 14px 6px;
    font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.085em;color:#6b7280;
}
.cp__staff{
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:9.5px;font-weight:400;
    text-transform:none;color:#8b949e;border:1px solid #dcdfe6;border-radius:3px;padding:1px 4px;
}
.cp__ch{
    display:flex;align-items:flex-start;gap:9px;width:100%;padding:8px 14px;text-align:left;
    background:none;border:0;border-left:3px solid transparent;cursor:pointer;
}
.cp__ch:hover{background:#f6f8fb}
.cp__ch.cp__on{background:#eef4ff;border-left-color:#2451a8}
.cp__hash{flex:0 0 15px;margin-top:2px;color:#8b949e;font-family:ui-monospace,Menlo,monospace;font-size:13px}
.cp__chmain{flex:1;min-width:0}
.cp__chn{display:flex;align-items:baseline;gap:6px;font-size:13px;color:#222;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__unread .cp__chn{font-weight:700}
.cp__arch{
    flex:0 0 auto;font-family:ui-monospace,Menlo,monospace;font-size:9.5px;color:#6b7280;
    background:#e8ebf0;border-radius:3px;padding:1px 4px;
}
.cp__chs{display:block;font-size:11.5px;color:#6b7280;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__chr{flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:4px;margin-top:1px}
.cp__cht{font-family:ui-monospace,Menlo,monospace;font-size:10px;color:#8b949e}
.cp__pill{
    min-width:19px;padding:1px 6px;border-radius:9px;background:#2451a8;color:#fff;
    font-family:ui-monospace,Menlo,monospace;font-size:10.5px;font-weight:700;text-align:center;
}
.cp__note{
    margin:10px 14px;padding:10px 12px;background:#f4f7f8;border:1px solid #dcdfe6;border-radius:4px;
    font-size:12px;color:#4a5562;line-height:1.5;
}

.cp__older{margin:10px 14px 4px;display:flex;flex-direction:column;gap:7px;align-items:flex-start}
.cp__olderb{
    padding:6px 12px;background:#fff;color:#2451a8;border:1px solid #dcdfe6;border-radius:4px;
    font-size:12px;font-weight:600;cursor:pointer;
}
.cp__olderb:hover{border-color:#2451a8;background:#eef4ff}
.cp__olderc{font-family:ui-monospace,Menlo,monospace;font-size:10.5px;color:#8b949e}
.cp__cap{
    margin:10px 14px 6px;padding:10px 12px;background:#eef4ff;border:1px solid #c9d9f5;
    border-left:3px solid #2451a8;border-radius:4px;font-size:12px;color:#1a2744;line-height:1.5;
}
.cp__cap strong{display:block;margin-bottom:2px}
.cp__where{font-family:ui-monospace,Menlo,monospace;font-size:11px;color:#2451a8}

.cp__day{
    display:flex;align-items:center;gap:10px;margin:14px 14px 8px;
    font-family:ui-monospace,Menlo,monospace;font-size:10px;letter-spacing:.06em;
    text-transform:uppercase;color:#8b949e;
}
.cp__day::before,.cp__day::after{content:"";flex:1;height:1px;background:#dcdfe6}

.cp__msg{display:flex;gap:9px;padding:5px 14px}
.cp__msg:hover{background:#fafbfd}
.cp__cont{padding-top:1px}
.cp__av{
    flex:0 0 28px;width:28px;height:28px;border-radius:4px;margin-top:2px;
    display:grid;place-items:center;color:#fff;font-size:10.5px;font-weight:700;
}
.cp__mmain{flex:1;min-width:0}
.cp__mh{display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;margin-bottom:1px}
.cp__mn{font-size:12.5px;font-weight:700;color:#222}
/* #2091 r5 — the Preview-as strip. Deliberately plain and slightly inset from
   the header so it reads as scaffolding for the preview rather than as chat
   chrome somebody might expect to ship. */
.cp__viewas{
  display:flex;align-items:center;gap:8px;flex-wrap:wrap;
  padding:6px 10px;border-bottom:1px solid #e4e7eb;background:#f7f8fa;
  font-size:11px;color:#52606d;
}
.cp__vl{font-weight:600;text-transform:uppercase;letter-spacing:.06em;white-space:nowrap}
.cp__vs{
  flex:1 1 120px;min-width:0;font:inherit;font-size:12px;color:#1f2933;
  padding:3px 6px;border:1px solid #cbd2d9;border-radius:4px;background:#fff;
}
.cp__vn{flex:1 1 100%;font-style:italic;color:#7b8794}
.cp__mrole{
    font-family:ui-monospace,Menlo,monospace;font-size:9.5px;padding:1px 4px;border-radius:3px;
    background:#e8ebf0;color:#4a5562;
}
.cp__staffrole{background:#eef4ff;color:#2451a8}
.cp__mt{font-family:ui-monospace,Menlo,monospace;font-size:10px;color:#8b949e}
.cp__mb{font-size:13.5px;line-height:1.46;color:#222;overflow-wrap:anywhere;white-space:pre-wrap}

.cp__lp{
    display:flex;gap:10px;align-items:flex-start;margin:6px 0 2px;padding:9px 10px;max-width:290px;
    background:#f4f7f8;border:1px solid #dcdfe6;border-left:3px solid #2451a8;border-radius:4px;
}
.cp__lpic{
    flex:0 0 26px;width:26px;height:26px;border-radius:3px;display:grid;place-items:center;
    background:#fff;border:1px solid #dcdfe6;font-size:12px;font-weight:700;color:#2451a8;
}
.cp__lpm{min-width:0}
.cp__lpt{font-size:12.5px;font-weight:600;color:#222;line-height:1.3}
.cp__lph{font-family:ui-monospace,Menlo,monospace;font-size:10.5px;color:#6b7280;margin-top:2px;overflow-wrap:anywhere}
.cp__lpw{margin-top:4px;font-size:10.5px;color:#9a6a00}
.cp__sani{
    display:flex;align-items:flex-start;gap:6px;margin-top:5px;
    font-family:ui-monospace,Menlo,monospace;font-size:10px;line-height:1.5;color:#6b7280;
}
.cp__sani::before{content:"";flex:0 0 5px;width:5px;height:5px;border-radius:50%;background:#fb8b03;margin-top:5px}

.cp__comp{border-top:1px solid #dcdfe6;padding:9px 12px 10px;background:#fff}
.cp__comp textarea{
    width:100%;min-height:62px;max-height:140px;resize:vertical;padding:8px 9px;
    font-size:13.5px;line-height:1.45;color:#222;background:#fff;border:1px solid #dcdfe6;border-radius:4px;
}
.cp__comp textarea:focus{border-color:#2451a8;outline:none}
.cp__atcap textarea{border-color:#a8252e}
.cp__compr{display:flex;align-items:center;gap:10px;margin-top:7px}
.cp__rules{flex:1;min-width:0;font-size:10.5px;color:#8b949e;line-height:1.35}
.cp__cnt{font-family:ui-monospace,Menlo,monospace;font-size:11px;color:#6b7280;white-space:nowrap}
.cp__warn{color:#9a6a00;font-weight:700}
.cp__over{color:#a8252e;font-weight:700}
.cp__send{
    padding:7px 15px;background:#2451a8;color:#fff;border:1px solid #2451a8;border-radius:4px;
    font-size:13px;font-weight:600;cursor:pointer;
}
.cp__send:hover{background:#1b3f85;border-color:#1b3f85}
.cp__send:disabled{background:#aab4c4;border-color:#aab4c4;cursor:not-allowed}
.cp__compmsg{margin-top:6px;font-size:11.5px;color:#a8252e}

/*
 * #2106 — the client's message keeps the line breaks the CA typed (cs3-chat
 * allows \n and refuses other control characters) and collapses nothing else.
 * `pre-line` rather than `pre-wrap` on purpose: this block also carries the
 * default wording, which is a sentence built in the component, and `pre-wrap`
 * would render the template's own indentation if it ever moved into the markup.
 */
.cp__closedm{white-space:pre-line}
.cp__ro{
    display:flex;gap:9px;align-items:flex-start;padding:11px 13px;
    background:#fff6e0;border-top:1px solid #fb8b03;font-size:12px;color:#6b4a00;line-height:1.5;
}
.cp__ro i{margin-top:2px;color:#fb8b03}
.cp__ro strong{display:block;color:#4d3500;margin-bottom:1px}

.cp__closed{border-top:1px solid #2451a8;background:#eef4ff;padding:11px 13px}
.cp__closedh{display:flex;align-items:flex-start;gap:8px}
.cp__closedh i{margin-top:2px;color:#2451a8}
.cp__closedh strong{font-size:12.5px;color:#1a2744;line-height:1.35}
.cp__closedb{margin:4px 0 0 23px;font-size:11.5px;color:#4a5562;line-height:1.45}
.cp__fake{
    margin:9px 0 0;padding:8px 9px;min-height:38px;background:#fff;
    border:1px dashed #dcdfe6;border-radius:4px;font-size:12.5px;color:#8b949e;
}

.cp__toast{
    position:fixed;right:20px;bottom:84px;z-index:1042;width:318px;max-width:calc(100vw - 40px);
    background:#fff;border:1px solid #dcdfe6;border-radius:6px;
    box-shadow:0 8px 26px rgba(26,39,68,.2);overflow:hidden;
}
.cp__toasth{
    display:flex;align-items:center;gap:7px;padding:9px 11px 0;
    font-size:10.5px;font-family:ui-monospace,Menlo,monospace;color:#6b7280;
}
.cp__toastw{color:#2451a8;font-weight:700}
.cp__toastcd{margin-left:auto}
.cp__toastb{display:flex;gap:9px;padding:7px 11px 10px}
.cp__toastb .cp__av{flex:0 0 26px;width:26px;height:26px}
.cp__toastn{font-size:12.5px;font-weight:700;color:#222}
.cp__toastx{font-size:12.5px;color:#4a5562;line-height:1.4;margin-top:1px}
.cp__toasta{display:flex;gap:7px;padding:0 11px 11px}
.cp__btn{
    padding:6px 12px;border-radius:4px;font-size:12px;font-weight:600;
    background:#fff;border:1px solid #dcdfe6;color:#4a5562;cursor:pointer;
}
.cp__btn:hover{border-color:#2451a8}
.cp__btn--p{background:#2451a8;border-color:#2451a8;color:#fff}
.cp__btn--p:hover{background:#1b3f85}
.cp__bar{height:3px;background:#e8ebf0}
.cp__bar i{display:block;height:100%;background:#fb8b03;transition:width 1s linear}

@media (max-width:560px){
  .cp__panel{width:100vw}
}

/* ── #2091 r4 — the pop-out shape ─────────────────────────────────────────
   Same component, filling its own window instead of hanging off the right of a
   page: no fixed positioning, no shadow, no border — the window frame is the
   edge now. */
.cp--window,
.cp--window .cp__panel{
    position:static;width:100%;height:100vh;max-width:none;
    border-left:0;box-shadow:none;
}
.cp--window .cp__panel{display:flex;flex-direction:column}

/* the "it is over there" pill left behind on the main page */
.cp__away{
    position:fixed;right:20px;bottom:20px;z-index:1040;
    display:flex;align-items:center;gap:10px;
    padding:10px 12px 10px 14px;
    background:#fff;border:1px solid #dcdfe6;border-left:3px solid #2451a8;border-radius:24px;
    box-shadow:0 5px 18px rgba(26,39,68,.18);
    font-size:12.5px;color:#4a5562;
}
.cp__away i{color:#2451a8}
.cp__awayb{
    padding:5px 12px;border-radius:14px;
    background:#2451a8;border:1px solid #2451a8;color:#fff;
    font-size:12px;font-weight:600;cursor:pointer;
}
.cp__awayb:hover{background:#1b3f85;border-color:#1b3f85}

.cp__blocked{
    display:flex;gap:8px;align-items:flex-start;
    padding:10px 13px;background:#fff6e0;border-bottom:1px solid #fb8b03;
    font-size:11.5px;line-height:1.45;color:#6b4a00;
}
.cp__blocked i{margin-top:2px;color:#fb8b03}

/* ── #2101 — office hours, the chime settings, and the flash ─────────────── */

.cp__hours{
    padding:8px 13px;background:#f3f6fb;border-bottom:1px solid #dcdfe6;
    font-size:11.5px;color:#374151;
}
/* Shut AND it applies to this viewer. A CA who may post at any time sees the
   client's hours without the panel implying they are locked out. */
.cp__hoursshut{background:#fff6e0;border-bottom-color:#fb8b03;color:#6b4a00}
.cp__hrow{display:flex;align-items:center;gap:7px;flex-wrap:wrap}
.cp__hrow i{color:#6b7280}
.cp__hoursshut .cp__hrow i{color:#fb8b03}
.cp__hrow strong{font-size:12px;font-weight:600}
.cp__hstate{
    padding:1px 7px;border-radius:9px;font-size:10px;font-weight:700;
    text-transform:uppercase;letter-spacing:.06em;
}
.cp__hopen{background:#dff3e6;color:#1d6b3a}
.cp__hshut{background:#f6dcdc;color:#8a2a2a}
.cp__hnext{color:#6b7280}
.cp__hoursshut .cp__hnext{color:#6b4a00}
.cp__hmore{
    margin-left:auto;padding:1px 7px;border:1px solid #c3ccda;border-radius:4px;
    background:#fff;color:#374151;font-size:10.5px;cursor:pointer;
}
.cp__hmore:hover{border-color:#2a56a8;color:#2a56a8}
.cp__hweek{margin-top:7px;padding-top:6px;border-top:1px solid #dcdfe6}
.cp__hday{display:flex;gap:8px;padding:1px 0;font-size:11px}
.cp__hdn{flex:0 0 34px;color:#6b7280;font-weight:600}
.cp__hdt{color:#374151}
.cp__hdshut{color:#9aa3af}
.cp__htoday .cp__hdn,.cp__htoday .cp__hdt{color:#1a2744;font-weight:700}
.cp__htz{margin-top:5px;font-size:10.5px;color:#6b7280}

.cp__icoon{background:rgba(255,255,255,.16);color:#fff}
.cp__notify{
    padding:10px 13px;background:#f3f6fb;border-bottom:1px solid #dcdfe6;
    font-size:11.5px;color:#374151;
}
.cp__nrow{display:flex;align-items:center;gap:8px;padding:3px 0;cursor:pointer}
.cp__nvol{cursor:default}
.cp__nvol label{flex:0 0 46px;color:#6b7280}
.cp__nvol input[type=range]{flex:1;min-width:0}
.cp__noff{opacity:.55}
.cp__ntest{
    padding:1px 8px;border:1px solid #c3ccda;border-radius:4px;background:#fff;
    color:#374151;font-size:10.5px;cursor:pointer;
}
.cp__ntest:hover:not(:disabled){border-color:#2a56a8;color:#2a56a8}
.cp__ntest:disabled{opacity:.5;cursor:default}
.cp__nnote{margin-top:6px;font-size:10.5px;line-height:1.45;color:#6b7280}

/* The launcher blinks with the tab title. `box-shadow` and `background` only —
   no transform, so the button does not move under a cursor that is about to
   click it, and nothing reflows. */
.cp__railflash{animation:cp-railflash 1.2s ease-in-out infinite}
@keyframes cp-railflash{
    0%,100%{background:#1a2744;box-shadow:0 5px 18px rgba(26,39,68,.34)}
    50%{background:#2a56a8;box-shadow:0 5px 22px rgba(42,86,168,.55)}
}
/* Respect the OS setting: a blinking control is exactly what this asks about,
   and the tab title still flashes for anyone who needs the signal. */
@media (prefers-reduced-motion:reduce){
    .cp__railflash{animation:none;background:#2a56a8}
}

/* ── #2098 — find a person, message them, and the online dots ───────────── */

.cp__find{padding:10px 13px 6px;border-bottom:1px solid #dcdfe6}
.cp__findr{display:flex;align-items:center;gap:7px;padding:5px 9px;
    border:1px solid #c3ccda;border-radius:5px;background:#fff}
.cp__findr:focus-within{border-color:#2a56a8;box-shadow:0 0 0 2px rgba(42,86,168,.14)}
.cp__findr i{color:#6b7280;font-size:12px}
.cp__findi{flex:1;min-width:0;border:0;outline:0;font-size:12.5px;color:#374151;background:transparent}
/* Safari draws its own clear button on type=search; ours is the one that also
   resets the result list and the presence watch. */
.cp__findi::-webkit-search-decoration,
.cp__findi::-webkit-search-cancel-button{-webkit-appearance:none;appearance:none}
.cp__findx{border:0;background:transparent;color:#6b7280;cursor:pointer;padding:0 2px}
.cp__findx:hover{color:#374151}
.cp__findn{padding:7px 2px 3px;font-size:11px;color:#6b7280}
.cp__finde{padding:7px 2px 3px;font-size:11px;line-height:1.45;color:#8a2a2a}

.cp__per{
    display:flex;align-items:center;gap:9px;width:100%;
    padding:7px 2px;border:0;background:transparent;cursor:pointer;text-align:left;
}
.cp__per:hover{background:#f3f6fb}
.cp__per:disabled{opacity:.6;cursor:default}
.cp__peav{position:relative;flex:0 0 28px}
.cp__pemain{flex:1;min-width:0}
.cp__pen{display:block;font-size:12.5px;color:#1a2744;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__per2{display:block;font-size:10.5px;color:#6b7280;margin-top:1px}
.cp__pecs{
    margin-left:4px;padding:0 5px;border-radius:8px;background:#e8eefb;color:#2451a8;
    font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;
}
.cp__pego{flex:0 0 auto;font-size:10.5px;font-weight:600;color:#2a56a8}

.cp__chav{position:relative;flex:0 0 26px}

/* The dot. Absent-means-offline is the common case — most people in a local
   have never opened chat — so "offline" is a quiet ring rather than a red mark:
   it says nothing is known, which is the truth. */
.cp__dot{
    position:absolute;right:-1px;bottom:-1px;width:9px;height:9px;border-radius:50%;
    background:#c3ccda;border:1.5px solid #fff;box-sizing:content-box;
}
.cp__doton{background:#2f9e5f}
.cp__hdot{
    display:inline-block;width:8px;height:8px;border-radius:50%;margin-left:6px;
    background:#9fb3d6;vertical-align:middle;
}
.cp__hdot.cp__doton{background:#6ddb9b}

/* ── #2102 — attachments: the paperclip, the queue, thumbnails and chips ─── */

.cp__file{display:none}
.cp__clip{
    width:28px;height:28px;flex:0 0 28px;border:1px solid #c3ccda;border-radius:4px;
    background:#fff;color:#374151;cursor:pointer;display:grid;place-items:center;
}
.cp__clip:hover:not(:disabled){border-color:#2a56a8;color:#2a56a8}
.cp__clip:disabled{opacity:.5;cursor:default}
/* The composer highlights while a file is over it. */
.cp__drag{box-shadow:inset 0 0 0 2px #2a56a8}

.cp__q{padding:6px 0 2px}
.cp__qr{display:flex;align-items:center;gap:7px;padding:3px 0;font-size:11.5px;color:#374151}
.cp__qr i{color:#6b7280}
.cp__qfail{color:#8a2a2a}
.cp__qfail i{color:#8a2a2a}
.cp__qn{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__qs{flex:0 0 auto;color:#6b7280;font-size:10.5px}
.cp__qbar{flex:0 0 54px;height:4px;border-radius:2px;background:#dde3ec;overflow:hidden}
.cp__qbar i{display:block;height:100%;background:#2a56a8;transition:width .15s linear}
.cp__qok{color:#2f9e5f}
.cp__qe{flex:0 0 auto;max-width:150px;font-size:10.5px;line-height:1.3}
.cp__qx{border:0;background:transparent;color:#6b7280;cursor:pointer;padding:0 2px}
.cp__qx:hover{color:#8a2a2a}
.cp__qerr{padding:3px 0 5px;font-size:11px;line-height:1.45;color:#8a2a2a}

.cp__atts{display:flex;flex-wrap:wrap;gap:6px;margin-top:5px}
.cp__thumb{
    padding:0;border:1px solid #dcdfe6;border-radius:5px;background:#f3f6fb;
    cursor:pointer;overflow:hidden;line-height:0;
}
.cp__thumb img{max-width:168px;max-height:168px;display:block}
.cp__thumb:hover{border-color:#2a56a8}
.cp__chip{
    display:flex;align-items:center;gap:8px;max-width:100%;
    padding:6px 9px;border:1px solid #dcdfe6;border-radius:5px;background:#f8fafc;
    cursor:pointer;text-align:left;
}
.cp__chip:hover{border-color:#2a56a8;background:#f3f6fb}
.cp__chipm{min-width:0}
.cp__chipn{display:block;font-size:11.5px;color:#1a2744;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__chips{display:block;font-size:10px;color:#6b7280;margin-top:1px}

.cp__lb{
    position:fixed;inset:0;z-index:1060;display:grid;place-items:center;
    background:rgba(16,24,40,.82);cursor:zoom-out;padding:24px;
}
.cp__lb img{max-width:92vw;max-height:84vh;border-radius:4px;background:#fff}
.cp__lbn{
    position:absolute;bottom:18px;left:0;right:0;text-align:center;
    color:#e8eefb;font-size:12px;
}
.cp__lbx{
    position:absolute;top:14px;right:16px;width:32px;height:32px;border:0;border-radius:4px;
    background:rgba(255,255,255,.14);color:#fff;cursor:pointer;
}
.cp__lbx:hover{background:rgba(255,255,255,.26)}
</style>
